/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ArrowLeft, Building2, Loader2, Trash2, Plus, Mail, Flag,
  Users, FlaskConical, Phone, Edit2, Search, Send, Handshake,
  CheckCircle2, Clock, ArrowRight, Share2, MessageSquare,
  Briefcase, FileText, Globe, ExternalLink, Star, Sparkles, Mic,
  Upload, Download, ShieldCheck, Eye, Pencil, AlertCircle, ClipboardCopy, MessageCircle, ChevronDown, ChevronRight,
  Ban, ShieldAlert
} from 'lucide-react';
import SpamCheckChip from '../components/SpamCheckChip';
import SentEmailDialog from '../components/SentEmailDialog';
import { toast } from 'sonner';

const API = '/api';

import { OUTREACH_STATUSES as CANONICAL_OUTREACH_STATUSES, OUTREACH_STATUS_COLORS } from '@/lib/statusColors';

const STATUS_OPTIONS = ['Active', 'On Hold', 'Closed - Won', 'Closed - Lost'];

// ── Response-status vocabulary (Aug 2026 unified) ─────────────────
// Formerly this page had its own `RESPONSE_STATUSES` list (Closed-Won /
// Closed-Lost / NCD Exchanged / Not Interested) that diverged from the
// canonical `OUTREACH_STATUSES` list used by the Outreach Tracker,
// Dashboard, and BD Funnel. That drift meant a status set here on the
// Accounts page (e.g. "Closed - Won") never surfaced in the Outreach
// dropdown or the BD funnel — the user's #1 source of funnel-mismatch
// bugs. The Add Outreach modal now uses the SAME 16 canonical statuses
// as everywhere else. Legacy stored values (Closed - Won / NCD
// Exchanged / etc.) are auto-remapped by `ACCT_STATUS_MAP` inside
// `routes/dashboard.py` so the funnel still counts historical data.
const RESPONSE_STATUSES = ['Prospect Added', ...CANONICAL_OUTREACH_STATUSES];
// Kept for badge coloring compatibility — augmented with the canonical
// color map so any status set from the outreach vocabulary shows the
// same chip color as it does on the Outreach Tracker.
const RESPONSE_COLORS = {
  ...OUTREACH_STATUS_COLORS,
  'Prospect Added': 'bg-slate-50 text-slate-600 border-slate-200',
  // Retain colors for legacy statuses (rendered on historical rows).
  'Interest Shown': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'NCD Exchanged': 'bg-blue-50 text-blue-700 border-blue-200',
  'Closed - Won': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Closed - Lost': 'bg-red-50 text-red-600 border-red-200',
  'Not Interested': 'bg-gray-50 text-gray-500 border-gray-200',
};const STATUS_COLORS = {
  'Active': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'On Hold': 'bg-amber-50 text-amber-700 border-amber-200',
  'Closed - Won': 'bg-blue-50 text-blue-700 border-blue-200',
  'Closed - Lost': 'bg-slate-100 text-slate-500 border-slate-200',
};

const OUTREACH_STATUS = ['Not Sent', 'Sent', 'Replied', 'In Discussion', 'Closed'];
const OUTREACH_ICONS = { 'Not Sent': Clock, 'Sent': Send, 'Replied': MessageSquare, 'In Discussion': Users, 'Closed': CheckCircle2 };
const OUTREACH_COLORS = {
  'Not Sent': 'text-slate-400 border-slate-200',
  'Sent': 'text-blue-500 border-blue-200',
  'Replied': 'text-amber-500 border-amber-200',
  'In Discussion': 'text-[#00709B] border-[#00709B]/30',
  'Closed': 'text-emerald-500 border-emerald-200',
};

const CDA_STATUSES = ['Not Started', 'Shared with Me', 'Shared with Them', 'Finalised', 'Did Not Insist'];
const BILATERAL_CDA_STATUSES = ['Not Started', 'Indicated', 'Shared with Pharma', 'Finalised', 'Signed', 'Did Not Insist'];

/**
 * Distil a pasted pharma rejection email into a short, scannable, THIRD-PERSON
 * reason for the Active Prospects list (AccountDetailPage).
 *
 * The raw `r.notes` field stores whatever the user pasted from Gmail — full
 * reply including greetings, thank-yous, and a long preamble in first-person
 * voice ("We're focusing on our internal asset..."). Dumping that verbatim
 * makes the list look like an inbox dump and forces the user to mentally
 * translate "we" → the pharma every time.
 *
 * This helper:
 *   1. Strips opening greetings ("Dear/Hi/Hello X,")
 *   2. Drops the first thank-you / acknowledgement sentence
 *   3. Drops trailing sign-offs ("Best regards", "Thanks", signature)
 *   4. **Rewrites first-person → third-person referencing the pharma name**
 *        - first "We're" / "we're"  → `${pharma} is`
 *        - first "We" / "we" (bare) → `${pharma}`
 *        - subsequent "we/We"       → "they/They"
 *        - "our/Our"                → "their/Their"
 *        - "us/Us"                  → "them/Them"
 *        - "I'm/I"                  → "they're/they"
 *        - "my/My"                  → "their/Their"
 *   5. Collapses whitespace + caps to 200 chars with a sentence-boundary cut.
 *
 * Pure function. Pharma name is required for the pronoun swap; if not
 * supplied, falls back to "the pharma" so we never leak first-person voice
 * into the rendered UI.
 */
const condenseDeclineReason = (raw, pharmaName) => {
  if (!raw || typeof raw !== 'string') return '';
  const pharma = (pharmaName || 'the pharma').trim() || 'the pharma';
  let t = raw.trim();
  // 1. Strip greeting line. Greeting word + at most ONE capitalized name
  //    word (NOT two — "Dear Amit Thank..." used to eat "Thank" as the
  //    second name token and break the thank-you strip downstream).
  t = t.replace(/^\s*(dear|hi|hello|hey|greetings)\b(\s+[A-Z][a-zA-Z'.-]*)?\s*[,.!?:]?\s*/i, '');
  // 2. Drop the first thank-you / acknowledgement sentence
  t = t.replace(
    /^\s*(thank(s| you)( so much| very much)?( for(?: (?:the|your|this|reaching out|getting in touch))?[^.\n]*)?[.!]?\s*)/i,
    ''
  );
  // 3. Drop sign-offs and everything after. Real pastes often have NO
  //    newline before "Best regards," (Gmail collapses paragraphs on
  //    plaintext copy), so we also match the inline form preceded by
  //    whitespace/comma.
  t = t.replace(/[\s\n,]*\b(best( regards)?|kind regards|sincerely|regards|cheers|warm regards|yours( sincerely)?|thanks( again)?)\b[\s\S]*$/i, '');
  // 4. Pronoun swap — first person → third person referencing the pharma.
  //    Order matters: contractions first (we're, I'm) so the bare-pronoun
  //    pass doesn't eat the "we" out of "we're".
  let firstWeReplaced = false;
  // First "We're" / "we're" → "<pharma> is"
  t = t.replace(/\bwe['’]re\b/gi, (m) => {
    if (!firstWeReplaced) { firstWeReplaced = true; return `${pharma} is`; }
    return m[0] === 'W' ? 'They’re' : 'they’re';
  });
  // Subsequent contractions
  t = t.replace(/\bwe['’]ve\b/gi, m => m[0] === 'W' ? 'They’ve' : 'they’ve');
  t = t.replace(/\bwe['’]ll\b/gi, m => m[0] === 'W' ? 'They’ll' : 'they’ll');
  t = t.replace(/\bwe['’]d\b/gi,  m => m[0] === 'W' ? 'They’d'  : 'they’d');
  // Bare "We" / "we" — first occurrence uses the pharma name, rest use "they"
  t = t.replace(/\b(we)\b/gi, (m) => {
    if (!firstWeReplaced) { firstWeReplaced = true; return m[0] === 'W' ? pharma : pharma; }
    return m[0] === 'W' ? 'They' : 'they';
  });
  // Possessive "our/Our" → "their/Their"
  t = t.replace(/\b(our)\b/gi, m => m[0] === 'O' ? 'Their' : 'their');
  t = t.replace(/\b(ours)\b/gi, m => m[0] === 'O' ? 'Theirs' : 'theirs');
  // Object "us/Us" → "them/Them"
  t = t.replace(/\b(us)\b/gi, m => m[0] === 'U' ? 'Them' : 'them');
  // First-person singular "I" → "they"  (handles the rare case where the
  // pharma rep writes in singular voice rather than royal-we)
  t = t.replace(/\bI['’]m\b/g, 'they’re');
  t = t.replace(/\b(I)\b/g, 'they');
  t = t.replace(/\b(my)\b/gi, m => m[0] === 'M' ? 'Their' : 'their');
  // 5. Whitespace cleanup
  t = t.replace(/\s+/g, ' ').trim();
  if (!t) return raw.trim().slice(0, 200) + (raw.length > 200 ? '…' : '');
  if (t.length <= 200) return t;
  const slice = t.slice(0, 200);
  const lastDot = Math.max(slice.lastIndexOf('. '), slice.lastIndexOf('? '), slice.lastIndexOf('! '));
  return (lastDot > 100 ? slice.slice(0, lastDot + 1) : slice) + '…';
};

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Account overview data
  const [project, setProject] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [pharmaContacts, setPharmaContacts] = useState([]);
  const [responses, setResponses] = useState([]);
  // Finalize discussion assets dialog (Jul 2026) — converts AI "Likely fit"
  // suggestions into the confirmed asset(s) actually being discussed.
  const [assetEditDialog, setAssetEditDialog] = useState(null);
  const [stats, setStats] = useState({});

  // Active tab
  const [activeTab, setActiveTab] = useState('responses');

  // Edit project
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Chat
  const [chatOpen, setChatOpen] = useState(false);

  // Account Introduce flow
  const [introduceResponse, setIntroduceResponse] = useState(null);
  // 'anonymous' = first-touch sounding to pharma only (biotech identity hidden);
  // 'named'     = full tripartite intro w/ biotech in CC (post-Interest Shown).
  const [introduceMode, setIntroduceMode] = useState('named');
  const [introduceBiotech, setIntroduceBiotech] = useState(null);
  const [introduceAssets, setIntroduceAssets] = useState([]);
  const [introduceSelectedAssets, setIntroduceSelectedAssets] = useState({});
  const [introduceContacts, setIntroduceContacts] = useState([]);
  const [introduceSelectedContacts, setIntroduceSelectedContacts] = useState({});
  const [introduceStep, setIntroduceStep] = useState(1); // 1=assets, 2=preview
  const [introduceSubject, setIntroduceSubject] = useState('');
  const [introduceBody, setIntroduceBody] = useState('');
  const [introduceDrafting, setIntroduceDrafting] = useState(false);
  const [introduceSending, setIntroduceSending] = useState(false);
  const introduceSendingRef = useRef(false);
  const [introduceLoading, setIntroduceLoading] = useState(false);
  const [introduceCcEmail, setIntroduceCcEmail] = useState('');
  const [introduceCcName, setIntroduceCcName] = useState('');
  const [introduceContext, setIntroduceContext] = useState('');

  // Response tracking
  const [showAddResponse, setShowAddResponse] = useState(false);
  const [responseSearch, setResponseSearch] = useState('');
  const [responseSearchResults, setResponseSearchResults] = useState([]);
  const [searchingResponse, setSearchingResponse] = useState(false);
  // Add Prospect step 2 — tag the asset(s) of interest for the intro
  const [prospectStep, setProspectStep] = useState(1);
  const [prospectBiotech, setProspectBiotech] = useState(null);
  const [prospectAssets, setProspectAssets] = useState([]);
  const [prospectSelectedAssets, setProspectSelectedAssets] = useState({});
  const [prospectAssetsLoading, setProspectAssetsLoading] = useState(false);
  const [prospectSiblingCount, setProspectSiblingCount] = useState(1);
  const [addingProspect, setAddingProspect] = useState(false);
  const responseTimeout = useRef(null);
  const [editingResponseId, setEditingResponseId] = useState(null);
  // Per-response "expand the full pasted reply" toggle for the Declined
  // rows. Default collapsed → user sees the distilled reason only.
  const [expandedReasonIds, setExpandedReasonIds] = useState(new Set());
  const toggleReasonExpanded = (id) => setExpandedReasonIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [editingEntryId, setEditingEntryId] = useState(null); // per_asset_statuses.entry_id; null = whole response
  const [editingEntryLabel, setEditingEntryLabel] = useState('');
  const [expandedResponseIds, setExpandedResponseIds] = useState(new Set());
  const [editResponseStatus, setEditResponseStatus] = useState('');
  const [editResponseNotes, setEditResponseNotes] = useState('');
  const [deleteResponseId, setDeleteResponseId] = useState(null);

  // Rejection follow-up close-the-loop flow (Feb 2026).
  // When the user marks a response as "Not Interested" and pastes the
  // pharma's rejection email, we generate an AI-drafted polite follow-up
  // TO the biotech contact explaining why the conversation didn't progress.
  // 2-step state machine inside the Update Response Status dialog:
  //   rejectionStep === 1 → paste rejection text + Save (status saves, draft fetches)
  //   rejectionStep === 2 → preview/edit AI draft + Send
  const [rejectionStep, setRejectionStep] = useState(1);
  const [rejectionEmailText, setRejectionEmailText] = useState('');
  const [rejectionDraftLoading, setRejectionDraftLoading] = useState(false);
  const [rejectionDraft, setRejectionDraft] = useState(null);  // {subject, body, to_email, to_name, biotech_company_name, asset_name}
  const [rejectionSending, setRejectionSending] = useState(false);
  const [rejectionTestSending, setRejectionTestSending] = useState(false);
  // The BD lead can opt to name the pharma explicitly when the biotech
  // already knows who we pitched to. Default off — the safer anonymised path.
  const [revealPharmaIdentity, setRevealPharmaIdentity] = useState(false);

  // Three-Way CDA state
  const [cdaUploading, setCdaUploading] = useState(null); // 'template' or 'final'

  // Status Update Email
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [showNextMatching, setShowNextMatching] = useState(false);
  const [nextMatchingNote, setNextMatchingNote] = useState('');
  const [savingNextMatching, setSavingNextMatching] = useState(false);
  const saveNextMatching = async () => {
    setSavingNextMatching(true);
    try {
      const { data } = await axios.put(`${API}/india-licensing/${id}`, { next_matching: { flagged: true, note: nextMatchingNote.trim() } }, { withCredentials: true });
      setProject(prev => ({ ...prev, next_matching: data?.next_matching || { flagged: true, note: nextMatchingNote.trim() } }));
      setShowNextMatching(false);
      toast.success('Lined up for matching — shown on Accounts and the Dashboard');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not flag account'); }
    finally { setSavingNextMatching(false); }
  };
  const clearNextMatching = async () => {
    try {
      await axios.put(`${API}/india-licensing/${id}`, { next_matching: { flagged: false } }, { withCredentials: true });
      setProject(prev => ({ ...prev, next_matching: { flagged: false } }));
      toast.success('Removed from matching queue');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear flag'); }
  };
  const stampMatchingRun = async () => {
    try {
      const { data } = await axios.post(`${API}/india-licensing/${id}/matching-run`, {}, { withCredentials: true });
      setProject(prev => ({ ...prev, last_matching_run_at: data?.last_matching_run_at, next_matching: { flagged: false } }));
    } catch { /* best-effort stamp */ }
  };
  const [statusUpdatePrompt, setStatusUpdatePrompt] = useState('');
  const [statusUpdateDrafting, setStatusUpdateDrafting] = useState(false);
  const [statusUpdateSubject, setStatusUpdateSubject] = useState('');
  const [statusUpdateBody, setStatusUpdateBody] = useState('');
  const [statusUpdateSending, setStatusUpdateSending] = useState(false);
  const [statusUpdateStep, setStatusUpdateStep] = useState(1); // 1=prompt, 2=preview
  const [statusUpdatePreviewMode, setStatusUpdatePreviewMode] = useState(false);

  // Outreach History — read-only aggregation of every outreach touchpoint
  // linked to this account's client profile (CRM intros + bulk sends).
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyCounts, setHistoryCounts] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [viewEmailRecord, setViewEmailRecord] = useState(null);

  const fetchOutreachHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/india-licensing/${id}/outreach-history`, { withCredentials: true });
      setHistoryRecords(data.records || []);
      setHistoryCounts(data.status_counts || {});
      setHistoryLoaded(true);
    } catch { toast.error('Failed to load outreach history'); }
    finally { setHistoryLoading(false); }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'outreach-history' && !historyLoaded) fetchOutreachHistory();
  }, [activeTab, historyLoaded, fetchOutreachHistory]);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/india-licensing/${id}/account-overview`, { withCredentials: true });
      setProject(data.project);
      setCompanyProfile(data.company_profile);
      setPharmaContacts(data.pharma_contacts || []);
      setResponses((data.responses || []).sort((a, b) => {
        if (a.introduced && !b.introduced) return -1;
        if (!a.introduced && b.introduced) return 1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }));
      setStats(data.stats || {});
    } catch {
      toast.error('Failed to load account');
      navigate('/accounts');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // ===== Response search (biotech companies) =====
  const handleResponseSearch = (val) => {
    setResponseSearch(val);
    if (responseTimeout.current) clearTimeout(responseTimeout.current);
    if (val.length < 2) { setResponseSearchResults([]); return; }
    setSearchingResponse(true);
    responseTimeout.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/india-licensing/search/biotech-for-response?q=${encodeURIComponent(val)}`, { withCredentials: true });
        setResponseSearchResults(data.results || []);
      } catch { setResponseSearchResults([]); }
      finally { setSearchingResponse(false); }
    }, 300);
  };

  const resetProspectDialog = () => {
    setShowAddResponse(false);
    setResponseSearch('');
    setResponseSearchResults([]);
    setProspectStep(1);
    setProspectBiotech(null);
    setProspectAssets([]);
    setProspectSelectedAssets({});
  };

  // Step 1 → 2: biotech picked, load its assets so the user can tag which
  // asset(s) form the basis of this introduction.
  const selectProspectBiotech = async (biotech) => {
    setProspectBiotech(biotech);
    setProspectStep(2);
    setProspectAssetsLoading(true);
    setProspectAssets([]);
    setProspectSelectedAssets({});
    try {
      const { data } = await axios.get(`${API}/india-licensing/prospect-assets/${biotech.id}`, { withCredentials: true });
      setProspectAssets(data.items || []);
      setProspectSiblingCount(data.matched_company_count || 1);
    } catch { toast.error('Failed to load assets for this biotech'); }
    finally { setProspectAssetsLoading(false); }
  };

  const addBiotechResponse = async () => {
    if (!prospectBiotech || addingProspect) return;
    const interestIds = Object.entries(prospectSelectedAssets).filter(([, v]) => v).map(([k]) => k);
    setAddingProspect(true);
    try {
      await axios.post(`${API}/india-licensing/${id}/responses`, {
        biotech_company_id: prospectBiotech.id,
        biotech_company_name: prospectBiotech.company_name,
        status: 'Prospect Added',
        interest_asset_ids: interestIds,
      }, { withCredentials: true });
      toast.success(`${prospectBiotech.company_name} added${interestIds.length ? ` · ${interestIds.length} asset(s) tagged` : ''}`);
      resetProspectDialog();
      fetchOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add response');
    } finally { setAddingProspect(false); }
  };

  const updateResponseStatus = async (opts = {}) => {
    const { skipBiotechEmail = false } = opts;
    if (!editingResponseId) return;
    setSaving(true);
    try {
      const payload = {
        status: editResponseStatus,
        // For Not Interested, we store the pasted pharma rejection text as
        // notes (so it's retrievable in the audit log) — caller can also
        // strip if they want.
        notes: editResponseStatus === 'Not Interested' && rejectionEmailText
          ? rejectionEmailText
          : editResponseNotes,
      };
      if (editingEntryId) payload.entry_id = editingEntryId;
      await axios.put(`${API}/india-licensing/${id}/responses/${editingResponseId}`, payload, { withCredentials: true });

      // Close-the-loop branch: status === Not Interested + rejection text
      // pasted → fetch AI draft → flip dialog to step 2 (review/send).
      // User can force-skip this by clicking "Save status only" which
      // passes `skipBiotechEmail: true` — useful when they want to record
      // the rejection privately without ever notifying the biotech.
      if (!skipBiotechEmail && editResponseStatus === 'Not Interested' && rejectionEmailText.trim().length >= 20) {
        setRejectionDraftLoading(true);
        try {
          const r = await axios.post(
            `${API}/india-licensing/${id}/responses/${editingResponseId}/draft-rejection-followup`,
            {
              rejection_email_text: rejectionEmailText,
              entry_id: editingEntryId || null,
              reveal_pharma_identity: revealPharmaIdentity,
            },
            { withCredentials: true }
          );
          setRejectionDraft(r.data);
          setRejectionStep(2);
          toast.success('Status saved — AI drafted a follow-up to the biotech. Review & Send.');
        } catch (err) {
          const msg = err?.response?.data?.detail || 'AI draft failed';
          toast.error(msg);
          // Status was already saved — just close the dialog
          setEditingResponseId(null);
          setEditingEntryId(null);
          setEditingEntryLabel('');
          fetchOverview();
        } finally {
          setRejectionDraftLoading(false);
        }
        return;
      }

      toast.success(editingEntryId ? `Status updated for ${editingEntryLabel || 'asset'}` : 'Status updated');
      setEditingResponseId(null);
      setEditingEntryId(null);
      setEditingEntryLabel('');
      fetchOverview();
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  // Send the user-reviewed close-the-loop email to the biotech contact.
  // Persists `rejection_followup_sent_at` on the response so the UI can
  // tag the row as "loop closed" going forward.
  const sendRejectionFollowup = async () => {
    if (!rejectionDraft) return;
    setRejectionSending(true);
    try {
      await axios.post(
        `${API}/india-licensing/${id}/responses/${editingResponseId}/send-rejection-followup`,
        {
          to_email: rejectionDraft.to_email,
          to_name: rejectionDraft.to_name,
          subject: rejectionDraft.subject,
          body: rejectionDraft.body,
          rejection_email_text: rejectionEmailText,
          entry_id: editingEntryId || null,
        },
        { withCredentials: true }
      );
      toast.success(`Sent to ${rejectionDraft.to_email} — biotech updated`);
      // Reset everything
      setEditingResponseId(null);
      setEditingEntryId(null);
      setEditingEntryLabel('');
      setRejectionStep(1);
      setRejectionEmailText('');
      setRejectionDraft(null);
      setRevealPharmaIdentity(false);
      fetchOverview();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Send failed';
      toast.error(msg);
    } finally {
      setRejectionSending(false);
    }
  };

  // Test-send the close-the-loop draft to the BD lead's own inbox so they can
  // verify formatting / signature / phrasing in Gmail before firing it at the
  // biotech contact. Does NOT persist `rejection_followup_sent_at` on the
  // response — only a real send writes the audit fields.
  const sendTestRejectionFollowup = async () => {
    if (!rejectionDraft) return;
    setRejectionTestSending(true);
    try {
      const r = await axios.post(
        `${API}/india-licensing/${id}/responses/${editingResponseId}/send-rejection-followup`,
        {
          to_email: rejectionDraft.to_email,
          to_name: rejectionDraft.to_name,
          subject: rejectionDraft.subject,
          body: rejectionDraft.body,
          rejection_email_text: rejectionEmailText,
          entry_id: editingEntryId || null,
          test_mode: true,
        },
        { withCredentials: true }
      );
      toast.success(`Test sent to ${r.data?.to_email || 'your inbox'} — check Gmail, then click "Send to biotech" when ready`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Test send failed';
      toast.error(msg);
    } finally {
      setRejectionTestSending(false);
    }
  };

  const deleteResponse = async () => {
    if (!deleteResponseId) return;
    try {
      await axios.delete(`${API}/india-licensing/${id}/responses/${deleteResponseId}`, { withCredentials: true });
      toast.success('Response removed');
      setDeleteResponseId(null);
      fetchOverview();
    } catch { toast.error('Failed to remove'); }
  };

  // ===== Three-Way CDA handlers =====
  const handleCdaStatusChange = async (responseId, newStatus) => {
    try {
      await axios.put(`${API}/india-licensing/${id}/responses/${responseId}`, {
        cda_status: newStatus,
      }, { withCredentials: true });
      toast.success(`Tripartite status updated to "${newStatus}"`);
      fetchOverview();
    } catch { toast.error('Failed to update status'); }
  };

  // ===== Bilateral CDA handlers =====
  const handleBilateralCdaStatusChange = async (responseId, newStatus) => {
    try {
      await axios.put(`${API}/india-licensing/${id}/responses/${responseId}`, {
        bilateral_cda_status: newStatus,
      }, { withCredentials: true });
      toast.success(`CDA status updated to "${newStatus}"`);
      fetchOverview();
    } catch { toast.error('Failed to update CDA status'); }
  };

  const handleBilateralCdaUpload = async (responseId, file) => {
    if (!file) return;
    setCdaUploading('bilateral');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'account_response');
      formData.append('entity_id', responseId);
      formData.append('doc_type', 'Bilateral CDA');
      const res = await axios.post(`${API}/documents/upload`, formData, { withCredentials: true });
      const docId = res.data?.id || '';
      await axios.put(`${API}/india-licensing/${id}/responses/${responseId}`, {
        bilateral_cda_doc_id: docId,
        bilateral_cda_status: 'Shared with Them',
      }, { withCredentials: true });
      toast.success('CDA document uploaded');
      fetchOverview();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file type' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Upload failed';
      toast.error(msg, { duration: 8000 });
      console.error('[bilateral CDA upload error]', { status, detail, err });
    }
    finally { setCdaUploading(null); }
  };

  const handleCdaUpload = async (responseId, file, docType) => {
    if (!file) return;
    setCdaUploading(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'account_response');
      formData.append('entity_id', responseId);
      formData.append('doc_type', docType === 'template' ? 'Three-Way CDA Template' : 'Three-Way CDA Signed');
      const res = await axios.post(`${API}/documents/upload`, formData, { withCredentials: true });
      const docId = res.data?.id || '';
      const updateField = docType === 'template' ? 'cda_template_doc_id' : 'cda_final_doc_id';
      await axios.put(`${API}/india-licensing/${id}/responses/${responseId}`, {
        [updateField]: docId,
        cda_status: docType === 'template' ? 'Under Review' : 'Signed',
      }, { withCredentials: true });
      toast.success(docType === 'template' ? 'CDA template uploaded' : 'Signed CDA uploaded');
      fetchOverview();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = (typeof detail === 'string' && detail)
        || (detail?.message)
        || (status === 413 ? 'File too large — try a smaller PDF (limit ~10MB)' : '')
        || (status === 422 ? 'Invalid file type' : '')
        || (status ? `Upload failed (HTTP ${status})` : err?.message)
        || 'Upload failed';
      toast.error(msg, { duration: 8000 });
      console.error('[CDA upload error]', { status, detail, err, docType });
    }
    finally {
      setCdaUploading(null);
    }
  };

  // ===== Account Introduce Flow =====
  // ── Finalize discussion assets (Jul 2026) ──────────────────────────
  const openAssetEditor = async (r) => {
    setAssetEditDialog({
      response: r, loading: true, assets: [], selected: new Set(),
      saving: false, newName: '', newStage: '', creating: false,
    });
    try {
      const { data } = await axios.get(
        `${API}/india-licensing/${id}/responses/${r.response_id}/biotech-assets`,
        { withCredentials: true }
      );
      const assets = data.assets || [];
      const interestIds = (r.interest_assets || []).map(a => a.id).filter(Boolean);
      const suggestedIds = (r.suggested_assets || []).map(a => a.id).filter(Boolean);
      const preselect = interestIds.length ? interestIds : suggestedIds;
      setAssetEditDialog(prev => prev && ({
        ...prev, loading: false, assets,
        selected: new Set(preselect.filter(pid => assets.some(a => a.id === pid))),
      }));
    } catch (e) {
      setAssetEditDialog(null);
      toast.error(e?.response?.data?.detail || 'Could not load this company\'s assets');
    }
  };

  const toggleAssetSelection = (assetId) => {
    setAssetEditDialog(prev => {
      if (!prev) return prev;
      const next = new Set(prev.selected);
      if (next.has(assetId)) next.delete(assetId); else next.add(assetId);
      return { ...prev, selected: next };
    });
  };

  const createManualAsset = async () => {
    const name = (assetEditDialog?.newName || '').trim();
    if (!name) { toast.error('Enter an asset name'); return; }
    setAssetEditDialog(prev => ({ ...prev, creating: true }));
    try {
      const { data } = await axios.post(`${API}/assets`, {
        company_id: assetEditDialog.response.biotech_company_id,
        asset_name: name,
        stage: (assetEditDialog.newStage || '').trim() || 'Pre-Clinical',
      }, { withCredentials: true });
      const created = { id: data.id, asset_name: name, stage: (assetEditDialog.newStage || '').trim() || 'Pre-Clinical' };
      setAssetEditDialog(prev => {
        const next = new Set(prev.selected); next.add(created.id);
        return { ...prev, creating: false, newName: '', newStage: '', assets: [...prev.assets, created], selected: next };
      });
      toast.success(`Asset "${name}" added to ${assetEditDialog.response.biotech_company_name}`);
    } catch (e) {
      setAssetEditDialog(prev => ({ ...prev, creating: false }));
      toast.error(e?.response?.data?.detail || 'Failed to add asset');
    }
  };

  const saveFinalAssets = async () => {
    setAssetEditDialog(prev => ({ ...prev, saving: true }));
    try {
      const { data } = await axios.put(
        `${API}/india-licensing/${id}/responses/${assetEditDialog.response.response_id}/assets`,
        { asset_ids: Array.from(assetEditDialog.selected) },
        { withCredentials: true }
      );
      setResponses(prev => prev.map(r => r.response_id === assetEditDialog.response.response_id
        ? { ...r, interest_assets: data.interest_assets, assets_finalized: data.assets_finalized,
            suggested_assets: data.assets_finalized ? [] : r.suggested_assets }
        : r));
      toast.success(data.assets_finalized
        ? `${data.interest_assets.length} discussion asset(s) finalized`
        : 'Selection cleared — reverting to AI "Likely fit" suggestions');
      setAssetEditDialog(null);
      if (!data.assets_finalized) fetchOverview();
    } catch (e) {
      setAssetEditDialog(prev => ({ ...prev, saving: false }));
      toast.error(e?.response?.data?.detail || 'Failed to save assets');
    }
  };

  const openIntroduce = async (response, mode = 'named') => {
    setIntroduceResponse(response);
    setIntroduceMode(mode);
    setIntroduceStep(1);
    setIntroduceLoading(true);
    setIntroduceSubject('');
    setIntroduceBody('');
    setIntroduceSelectedAssets({});
    setIntroduceSelectedContacts({});
    setIntroduceContext('');
    // Pre-fill TO from account's pharma contact (this var is reused as the
    // single recipient in both modes — the field name "ccEmail" predates the
    // anonymous flow and stays for diff hygiene).
    setIntroduceCcEmail(project?.client_email || '');
    setIntroduceCcName(project?.client_name || '');
    try {
      const { data } = await axios.get(
        `${API}/india-licensing/${id}/responses/${response.response_id}/biotech-assets`,
        { withCredentials: true }
      );
      setIntroduceBiotech(data.company);
      // Sort assets by clinical stage from highest → lowest so the most
      // mature candidates show first in the picker.
      const stageRank = (stage) => {
        const s = (stage || '').toLowerCase();
        if (s.includes('market') || s === 'approved') return 100;
        if (s.includes('phase 3') || s.includes('phase iii')) return 80;
        if (s.includes('phase 2') || s.includes('phase ii')) return 70;
        if (s.includes('phase 1') || s.includes('phase i')) return 60;
        if (s.includes('ind') || s.includes('investigational')) return 50;
        if (s.includes('pre-clinical') || s.includes('preclinical')) return 40;
        if (s.includes('discovery') || s.includes('lead')) return 30;
        return 0;
      };
      const sortedAssets = [...(data.assets || [])].sort(
        (a, b) => stageRank(b.stage) - stageRank(a.stage)
      );
      setIntroduceAssets(sortedAssets);
      setIntroduceContacts(data.company?.contacts || []);
      // Smart pre-selection — backend returns `priority_match_asset_ids` based
      // on the pharma contact's stated interest_areas (their specific
      // indications + TAs from `pharma_contacts.interest_areas` and the
      // parent `company_profile.therapeutic_interests`). We pre-tick only
      // those; the user can untick or tick more as needed.
      //   - null  → backend has no priorities on file → fall back to
      //             "tick all" (old behaviour, safe)
      //   - []    → priorities exist but zero matches → keep all unticked so
      //             the user is forced to think about it
      //   - [..]  → tick exactly those
      const priorityIds = data.priority_match_asset_ids;
      const asel = {};
      if (Array.isArray(priorityIds)) {
        const matchSet = new Set(priorityIds);
        sortedAssets.forEach(a => { asel[a.id] = matchSet.has(a.id); });
        if (data.interest_tagged && priorityIds.length > 0) {
          toast.success(`Pre-selected ${priorityIds.length} tagged asset(s) — the basis of this introduction`, { duration: 4000 });
        } else if (priorityIds.length === 0 && (data.priority_match_terms_count || 0) > 0) {
          toast.warning(`No assets match ${data.pharma_contact_name || 'this contact'}'s stated priorities — tick manually if needed`, { duration: 5000 });
        } else if (priorityIds.length > 0) {
          toast.success(`Pre-selected ${priorityIds.length} of ${sortedAssets.length} asset(s) matching ${data.pharma_contact_name || 'contact'}'s priorities`, { duration: 4000 });
        }
      } else {
        sortedAssets.forEach(a => { asel[a.id] = true; });
      }
      setIntroduceSelectedAssets(asel);
      const csel = {};
      if (mode === 'named') {
        (data.company?.contacts || []).forEach(c => { if (c.email) csel[c.email] = true; });
      }
      setIntroduceSelectedContacts(csel);
    } catch { toast.error('Failed to load biotech data'); setIntroduceResponse(null); }
    finally { setIntroduceLoading(false); }
  };

  const handleIntroduceDraft = async () => {
    const selectedIds = Object.entries(introduceSelectedAssets).filter(([, v]) => v).map(([k]) => k);
    if (selectedIds.length === 0) { toast.error('Select at least one asset'); return; }
    setIntroduceDrafting(true);
    try {
      const { data } = await axios.post(
        `${API}/india-licensing/${id}/responses/${introduceResponse.response_id}/draft-introduce`,
        { asset_ids: selectedIds, context: introduceContext, mode: introduceMode },
        { withCredentials: true }
      );
      setIntroduceSubject(data.subject || '');
      setIntroduceBody(data.body || '');
      setIntroduceStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to draft email'); }
    finally { setIntroduceDrafting(false); }
  };

  const handleIntroduceSend = async () => {
    if (introduceSendingRef.current) return;
    if (!introduceCcEmail) { toast.error('No pharma contact email'); return; }
    const ccContacts = introduceContacts.filter(c => introduceSelectedContacts[c.email]).map(c => ({ name: c.name, email: c.email }));
    const selectedAssetIds = Object.entries(introduceSelectedAssets).filter(([, v]) => v).map(([k]) => k);
    introduceSendingRef.current = true;
    setIntroduceSending(true);
    try {
      const { data } = await axios.post(
        `${API}/india-licensing/${id}/responses/${introduceResponse.response_id}/send-introduce`,
        {
          recipients: [{ name: introduceCcName, email: introduceCcEmail }],
          cc_contacts: introduceMode === 'anonymous' ? [] : ccContacts,
          subject: introduceSubject,
          body: introduceBody,
          mode: introduceMode,
          asset_ids: selectedAssetIds,
        },
        { withCredentials: true, timeout: 30000 }
      );
      const successMsg = introduceMode === 'anonymous'
        ? `Anonymous sounding sent to ${introduceCcName} — biotech identity withheld. Status flipped to Introduction Made.`
        : `Introduction sent to ${introduceCcName} with ${ccContacts.length} biotech contact(s) in CC. Status flipped to NCD Exchanged.`;
      toast.success(successMsg);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Gmail not connected. Please connect Gmail first.');
      } else if (err?.response?.status === 400) {
        toast.error(err?.response?.data?.detail || 'Request failed');
      } else {
        toast.info('Email is being processed — check your Gmail Sent folder to confirm.');
      }
    } finally {
      setIntroduceSending(false);
      introduceSendingRef.current = false;
      setIntroduceResponse(null);
      try { fetchOverview(); } catch {}
    }
  };

  const [introduceTestSending, setIntroduceTestSending] = useState(false);
  const handleIntroduceTestSend = async () => {
    if (!introduceSubject || !introduceBody) { toast.error('Draft email first'); return; }
    setIntroduceTestSending(true);
    try {
      await axios.post(
        `${API}/india-licensing/${id}/responses/${introduceResponse.response_id}/send-introduce`,
        {
          recipients: [{ name: 'Test', email: '__TEST__' }],
          cc_email: '',
          cc_name: '',
          subject: `[TEST] ${introduceSubject}`,
          body: introduceBody,
          test_mode: true,
          mode: introduceMode,
        },
        { withCredentials: true, timeout: 30000 }
      );
      toast.success('Test email queued — check your inbox shortly');
    } catch {
      toast.info('Test email is being processed — check your inbox.');
    } finally { setIntroduceTestSending(false); }
  };

  // ===== Project CRUD =====
  const handleEditProject = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/india-licensing/${id}`, editForm, { withCredentials: true });
      toast.success('Account updated');
      setShowEdit(false);
      fetchOverview();
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const handleShareProject = async () => {
    try {
      const token = project?.share_token;
      if (!token) { toast.error('Share token not available'); return; }
      const url = `${window.location.origin}/il-status/${token}`;
      await navigator.clipboard.writeText(url);
      window.open(url, '_blank');
      toast.success('Share link copied & opened');
    } catch { toast.error('Failed to generate share link'); }
  };

  // ===== Revoke / Restore public share-link access =====
  // Used when the pharma BD lead has declined to move forward and the
  // BD lead wants to immediately cut off the pharma's access to the live
  // status page. Revocation is REVERSIBLE — the share_token stays intact
  // so previously-sent URLs auto-reactivate on restore.
  // ===== Status Update Email =====
  const handleDraftStatusUpdate = async () => {
    setStatusUpdateDrafting(true);
    try {
      const res = await axios.post(`${API}/india-licensing/${id}/status-update-email`, {
        voice_prompt: statusUpdatePrompt,
      }, { withCredentials: true });
      setStatusUpdateSubject(res.data.subject);
      setStatusUpdateBody(res.data.body);
      setStatusUpdateStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to draft update'); }
    finally { setStatusUpdateDrafting(false); }
  };

  const handleSendStatusUpdate = async () => {
    if (!statusUpdateBody.trim()) return;
    setStatusUpdateSending(true);
    try {
      // PRIMARY RECIPIENT = the account's designated contact (project.client_*).
      // The bug we're fixing (2026-06-06): previously the code used
      // `pharmaContacts[0]?.email`, which is the first entry of a company-wide
      // list (every BD person at Viatris, etc.) — a Viatris In-licensing
      // account routed the update email to `brett.roberts` instead of the
      // canonical contact Zafeirios Anagnostopoulos shown in the page header.
      // The email body addressed Zafeirios by name while the TO header was a
      // random colleague — a serious confidentiality risk.
      const projectEmail = (project?.client_email || '').trim();
      const projectName = (project?.client_name || '').trim();
      let toEmail = projectEmail;
      let toName = projectName;
      // Fall back to the first company contact ONLY if the account has no
      // designated email at all (very old records). Surface a clear toast
      // so the user knows we're using a non-canonical recipient.
      if (!toEmail) {
        toEmail = pharmaContacts[0]?.email || '';
        toName = pharmaContacts[0]?.name || projectName;
        if (toEmail) {
          toast.info(`No designated contact set — sending to ${toName || toEmail} (first Viatris contact). Edit the account to set a canonical contact.`, { duration: 6000 });
        }
      }
      if (!toEmail) {
        toast.error('No contact email found on this account. Edit the account to add one.');
        setStatusUpdateSending(false);
        return;
      }

      // Last-mile guard: the body addresses someone by name, so make sure the
      // greeting matches the TO. If they diverge (e.g. user edited the body
      // after the dialog opened, or the account contact was swapped), warn
      // and require confirmation before sending.
      const greetingMatch = statusUpdateBody.match(/^\s*Dear\s+([^\n,]+)/i);
      const greetingName = greetingMatch ? greetingMatch[1].trim() : '';
      if (greetingName && toName && greetingName.toLowerCase() !== toName.toLowerCase() && !greetingName.toLowerCase().includes(toName.split(/\s+/)[0].toLowerCase())) {
        const proceed = window.confirm(
          `The email greeting says "Dear ${greetingName}" but the recipient is "${toName} <${toEmail}>". Send anyway?`
        );
        if (!proceed) {
          setStatusUpdateSending(false);
          return;
        }
      }

      await axios.post(`${API}/gmail/send`, {
        to: toEmail,
        to_name: toName,
        subject: statusUpdateSubject,
        body: statusUpdateBody,
      }, { withCredentials: true });

      toast.success(`Status update sent to ${toName || toEmail}`);
      setShowStatusUpdate(false);
      setStatusUpdateStep(1);
      setStatusUpdatePrompt('');
      setStatusUpdateBody('');
      setStatusUpdateSubject('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); }
    finally { setStatusUpdateSending(false); }
  };

  if (loading || !project) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="account-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const tabs = [
    { key: 'responses', label: 'Active Prospects', count: responses.length },
    { key: 'outreach-history', label: 'Outreach History', count: historyLoaded ? historyRecords.length : (stats.outreached || 0) },
    { key: 'overview', label: 'Company Overview' },
  ];

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="account-detail-page">
      {/* Back link */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/accounts')} className="h-8 px-2 -ml-2 text-xs text-slate-500 hover:text-slate-800" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Accounts
        </Button>
      </div>

      {/* Header — account name + client info absolute-left on top, action
          buttons in their own wrap-friendly row below so additional buttons
          (Edit / Share / Send Update / etc.) don't squeeze the title column.
          Mirrors the PharmaContactDetailPage header pattern for cross-CRM UX
          consistency. */}
      <div className="mb-2">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-3">
          <h1
            className="text-3xl font-bold tracking-tight text-slate-900 whitespace-nowrap"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="account-title"
          >
            {project.project_name}
          </h1>
          <Badge variant="outline" className={`text-[11px] px-2 py-0.5 ${STATUS_COLORS[project.status] || ''}`} data-testid="account-status">
            {project.status}
          </Badge>
          {project.client_company && (
            <span className="flex items-center gap-1 text-sm text-slate-500">
              <Building2 className="w-3.5 h-3.5" />{project.client_company}
            </span>
          )}
          {project.client_name && (
            project.client_pharma_contact_id ? (
              <a
                href={`/pharma/${project.client_pharma_contact_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#00709B] hover:underline"
                title={`Open ${project.client_name}'s CRM profile in a new tab`}
                data-testid="header-client-contact-link"
              >
                · {project.client_name}
              </a>
            ) : (
              <span className="text-sm text-slate-600">· {project.client_name}</span>
            )
          )}
        </div>
        {/* Contact preference chips — TA + stage only. Modality chips removed
            on user request (2026-07-21): dozens of synonym-heavy purple chips
            made the header unreadable. Modalities remain visible/editable on
            the contact profile and Edit Account. */}
        {((project.client_therapeutic_areas || []).length > 0 || (project.client_preferred_stages || []).length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="account-preference-chips">
            {(project.client_therapeutic_areas || []).map((t) => (
              <Badge key={`ta-${t}`} variant="outline" className="text-[10px] px-1.5 py-0 bg-sky-50 text-sky-700 border-sky-200" data-testid={`detail-ta-${t}`}>{t}</Badge>
            ))}
            {(project.client_preferred_stages || []).map((s) => (
              <Badge key={`stg-${s}`} variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`detail-stage-${s}`}>{s}</Badge>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => { setEditForm({ ...project }); setShowEdit(true); }}
            className="bg-violet-600 text-white hover:bg-violet-700"
            data-testid="edit-account-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Edit Account
          </Button>
          <Button
            onClick={handleShareProject}
            variant="outline"
            className="border-[#00709B]/30 text-[#00709B] hover:bg-[#00709B]/5"
            data-testid="share-account-btn"
            title="Open & copy the public share URL"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share Link
          </Button>
          <Button
            onClick={() => { setShowStatusUpdate(true); setStatusUpdateStep(1); setStatusUpdatePrompt(''); stampMatchingRun(); }}
            variant="outline"
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="status-update-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> Send Update
          </Button>
          <Button
            onClick={() => { if (project.next_matching?.flagged) clearNextMatching(); else { setNextMatchingNote(project.next_matching?.note || ''); setShowNextMatching(true); } }}
            variant="outline"
            className={project.next_matching?.flagged ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-slate-200 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'}
            title={project.next_matching?.flagged ? 'Flagged: run matching next — click to clear' : 'Line this account up for the next matching run'}
            data-testid="toggle-next-matching-btn"
          >
            <Flag className={`w-4 h-4 mr-2 ${project.next_matching?.flagged ? 'fill-emerald-500' : ''}`} /> {project.next_matching?.flagged ? 'Matching next' : 'Run matching next'}
          </Button>
        </div>
        {project.last_matching_run_at && (
          <p className="text-[11px] text-slate-400 mt-1" data-testid="last-matching-run">Last matching run {new Date(project.last_matching_run_at).toLocaleString()}{project.last_matching_run_by ? ` · ${project.last_matching_run_by.split('@')[0]}` : ''}</p>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Outreach Sent', value: stats.outreached || 0, icon: Send, color: 'text-violet-600', bg: 'bg-violet-50' },
          { label: 'Introductions', value: stats.responses_count || 0, icon: Handshake, color: 'text-cyan-600', bg: 'bg-cyan-50' },
        ].map((s, i) => (
          <Card key={i} className="border-slate-100">
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
                <p className="text-[10px] text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Client Info Bar */}
      <Card className="border-slate-100" data-testid="client-info-card">
        <CardContent className="p-4 flex items-center gap-6">
          <div className="w-10 h-10 rounded-lg bg-[#00709B]/10 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-[#00709B]" />
          </div>
          <div className="flex-1 grid grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Company</p>
              <p className="text-sm font-semibold text-slate-800 mt-0.5">{project.client_company}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Contact</p>
              {project.client_name ? (
                project.client_pharma_contact_id ? (
                  <a
                    href={`/pharma/${project.client_pharma_contact_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00709B] hover:underline mt-0.5 inline-flex items-center gap-1"
                    title={`Open ${project.client_name}'s CRM profile in a new tab`}
                    data-testid="client-contact-link"
                  >
                    {project.client_name}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ) : project.client_biotech_id ? (
                  <a
                    href={project.client_biotech_contact_id
                      ? `/biotech/${project.client_biotech_id}#contact-${project.client_biotech_contact_id}`
                      : `/biotech/${project.client_biotech_id}#contacts`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#00709B] hover:underline mt-0.5 inline-flex items-center gap-1"
                    title={`Open ${project.client_company} → contacts`}
                    data-testid="client-contact-link"
                  >
                    {project.client_name}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ) : (
                  <p className="text-sm text-slate-700 mt-0.5" data-testid="client-contact-text">{project.client_name}</p>
                )
              ) : (
                <p className="text-sm text-slate-400 mt-0.5">—</p>
              )}
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Email</p>
              {project.client_email ? (
                <div className="mt-0.5 flex items-center gap-1.5 group">
                  <a href={`mailto:${project.client_email}`} className="text-sm text-[#00709B] hover:underline inline-flex items-center gap-1" data-testid="client-email-link">
                    <Mail className="w-3 h-3" /> {project.client_email}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(project.client_email)
                        .then(() => toast.success(`Copied ${project.client_email}`))
                        .catch(() => toast.error('Copy failed'));
                    }}
                    title="Copy email"
                    className="text-slate-300 hover:text-[#00709B] opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="client-email-copy-btn"
                  >
                    <ClipboardCopy className="w-3 h-3" />
                  </button>
                </div>
              ) : <p className="text-sm text-slate-400 mt-0.5">—</p>}
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Phone</p>
              {project.client_phone ? (
                <p className="text-sm text-slate-700 mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" /> {project.client_phone}</p>
              ) : <p className="text-sm text-slate-400 mt-0.5">—</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === t.key
                ? 'text-[#00709B]'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${t.key}`}>
            {t.label}
            {t.count !== undefined && (
              <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">{t.count}</Badge>
            )}
            {activeTab === t.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00709B] rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* ===== TAB: Active Prospects ===== */}
      {activeTab === 'responses' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 font-medium">
              Biotech companies that responded to outreach on behalf of <span className="font-semibold text-slate-800">{project.client_company}</span>
            </p>
            <Button size="sm" onClick={() => { setShowAddResponse(true); setResponseSearch(''); setResponseSearchResults([]); }}
              className="h-8 text-xs bg-[#0A1128] text-white hover:bg-slate-800" data-testid="add-response-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Prospect
            </Button>
          </div>

          {responses.length === 0 ? (
            <Card className="border-dashed border-slate-200">
              <CardContent className="py-10 text-center">
                <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No responses tracked yet</p>
                <p className="text-xs text-slate-400 mt-1">Manage biotech companies actively being prospected</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Column headers */}
              <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50/50 rounded-t-lg">
                <div className="w-9 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</span>
                </div>
                <div className="w-px h-4 bg-slate-300" />
                <div className="shrink-0 w-[120px] text-center">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span>
                </div>
                <div className="w-px h-4 bg-slate-300" />
                <div className="shrink-0 w-[100px] text-center">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</span>
                </div>
              </div>
              {responses.map(r => {
                const isIntroduced = !!r.introduced;
                // Row color reflects the REAL state of the conversation:
                // Granular status → row tint mapping (matches RESPONSE_COLORS
                // semantics but applied to the card chrome instead of just
                // the badge). Lets a glance tell you the funnel position.
                const POSITIVE_STATUSES = ['Interest Shown', 'NCD Exchanged', 'Asset Under Evaluation', 'Meeting Scheduled', 'Deal Discussion', 'Closed - Won'];
                const LOST_STATUSES = ['Closed - Lost', 'Not Interested', 'Already in Touch'];
                const isPositive = POSITIVE_STATUSES.includes(r.status);
                const isLost = LOST_STATUSES.includes(r.status);
                // Aug 2026 · Cross-page DNC surfacing. If the underlying
                // biotech has been flagged "Do Not Contact" on its detail
                // page, we must:
                //   1. Red-tint the row so a glance catches it
                //   2. Show a "DO NOT CONTACT" badge with hover reason
                //   3. Hide the "Introduce Anonymously" primary action
                //      (silent risk of shipping a promotional intro to an
                //      opted-out company otherwise).
                const dnc = r.do_not_contact || {};
                const isDNC = !!dnc.flagged;
                const dncReason = (dnc.reason || '').trim();
                // Snooze is softer: temporary hold. Show yellow warn treatment.
                const snz = r.snooze || {};
                const now = new Date();
                const snoozeUntil = snz.until ? new Date(snz.until) : null;
                const isSnoozed = !!(snz.enabled && snoozeUntil && snoozeUntil > now);
                const rowTint = ({
                  'Prospect Added':         { card: 'border-slate-100 hover:border-[#00709B]/20',                  ico: 'bg-slate-50',    icoFg: 'text-slate-500'   },
                  'Introduction Made':      { card: 'border-cyan-200 bg-cyan-50/40',                                ico: 'bg-cyan-100',    icoFg: 'text-cyan-700'    },
                  'Email Acknowledged':     { card: 'border-lime-200 bg-lime-50/40',                                ico: 'bg-lime-100',    icoFg: 'text-lime-700'    },
                  'Interest Shown':         { card: 'border-emerald-300 border-l-4 border-l-emerald-500 bg-emerald-50/50', ico: 'bg-emerald-100', icoFg: 'text-emerald-700' },
                  'NCD Exchanged':          { card: 'border-blue-300 bg-blue-50/40',                                ico: 'bg-blue-100',    icoFg: 'text-blue-700'    },
                  'Asset Under Evaluation': { card: 'border-amber-300 bg-amber-50/40',                              ico: 'bg-amber-100',   icoFg: 'text-amber-700'   },
                  'Meeting Requested':      { card: 'border-violet-200 bg-violet-50/30',                            ico: 'bg-violet-50',   icoFg: 'text-violet-600'  },
                  'Meeting Scheduled':      { card: 'border-violet-300 bg-violet-50/40',                            ico: 'bg-violet-100',  icoFg: 'text-violet-700'  },
                  'CDA in Progress':        { card: 'border-blue-200 bg-blue-50/30',                                ico: 'bg-blue-50',     icoFg: 'text-blue-600'    },
                  'Deal Discussion':        { card: 'border-purple-300 bg-purple-50/50',                            ico: 'bg-purple-100',  icoFg: 'text-purple-700'  },
                  'Closed - Won':           { card: 'border-emerald-400 border-l-4 border-l-emerald-600 bg-emerald-100/40', ico: 'bg-emerald-200', icoFg: 'text-emerald-800' },
                  'Closed - Lost':          { card: 'border-slate-200 border-l-4 border-l-red-500 bg-red-50/40',    ico: 'bg-red-100',     icoFg: 'text-red-600'     },
                  'Not Interested':         { card: 'border-slate-200 bg-gray-50/60',                               ico: 'bg-gray-100',    icoFg: 'text-gray-500'    },
                  'Already in Touch':       { card: 'border-slate-200 border-l-4 border-l-red-500 bg-red-50/40',    ico: 'bg-red-100',     icoFg: 'text-red-600'     },
                })[r.status] || { card: 'border-slate-100 hover:border-[#00709B]/20', ico: 'bg-slate-50', icoFg: 'text-slate-500' };
                // DNC/Snooze override the row tint completely so it dominates
                // the visual (opt-out signal always wins over funnel status).
                const dncTint = isDNC
                  ? { card: 'border-red-300 border-l-4 border-l-red-600 bg-red-50/60', ico: 'bg-red-100', icoFg: 'text-red-700' }
                  : isSnoozed
                    ? { card: 'border-amber-300 border-l-4 border-l-amber-500 bg-amber-50/40', ico: 'bg-amber-100', icoFg: 'text-amber-700' }
                    : null;
                const cardBg = (dncTint || rowTint).card;
                const iconBg = (dncTint || rowTint).ico;
                const iconColor = (dncTint || rowTint).icoFg;
                return (
                <React.Fragment key={r.response_id}>
                <Card className={`transition-colors ${cardBg}`} data-testid={`response-${r.response_id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    {Array.isArray(r.per_asset_statuses) && r.per_asset_statuses.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setExpandedResponseIds(prev => {
                          const next = new Set(prev);
                          if (next.has(r.response_id)) next.delete(r.response_id);
                          else next.add(r.response_id);
                          return next;
                        })}
                        className="w-5 h-5 -mr-1 shrink-0 flex items-center justify-center text-slate-400 hover:text-slate-700"
                        title={`${r.per_asset_statuses.length} asset(s) — click to ${expandedResponseIds.has(r.response_id) ? 'collapse' : 'expand'}`}
                        data-testid={`toggle-per-asset-${r.response_id}`}
                      >
                        {expandedResponseIds.has(r.response_id)
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />}
                      </button>
                    ) : <div className="w-0 shrink-0" />}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
                      <Building2 className={`w-4 h-4 ${iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold truncate ${isDNC ? 'text-red-800' : isLost ? 'text-red-700' : 'text-slate-800'}`}>{r.biotech_company_name}</span>
                        {/* Aug 2026 · DO NOT CONTACT badge cascaded from the
                            biotech's detail-page flag. Hovering shows the
                            reason the BD lead recorded. Clicking navigates
                            to the biotech page for edit/remove. */}
                        {isDNC && (
                          <a
                            href={r.biotech_company_id ? `/biotech/${r.biotech_company_id}` : '#'}
                            onClick={(e) => { if (!r.biotech_company_id) e.preventDefault(); }}
                            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-red-300 bg-red-100 text-[9.5px] font-semibold uppercase tracking-wider text-red-700 hover:bg-red-200"
                            title={dncReason || 'This biotech has opted out of outreach. Click to review the flag on the biotech page.'}
                            data-testid={`dnc-badge-${r.response_id}`}
                          >
                            <Ban className="w-2.5 h-2.5" />
                            Do Not Contact
                          </a>
                        )}
                        {!isDNC && isSnoozed && (
                          <span
                            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-[9.5px] font-semibold uppercase tracking-wider text-amber-800"
                            title={`Snoozed until ${snoozeUntil.toLocaleDateString()}${(snz.reason ? ` · ${snz.reason}` : '')}`}
                            data-testid={`snooze-badge-${r.response_id}`}
                          >
                            <Clock className="w-2.5 h-2.5" />
                            Snoozed
                          </span>
                        )}
                        {r.location && <span className="text-[10px] text-slate-400 shrink-0">{r.location}</span>}
                        {/* Biotech primary contact — rendered inline next to
                            the company name so the row stays compact and the
                            user can copy/click the email without scanning a
                            separate column. */}
                        {(r.biotech_primary_contact?.name || r.biotech_primary_contact?.email) && (
                          <span className="inline-flex items-center gap-1 shrink-0 text-[10px] text-slate-500 pl-2 border-l border-slate-200" data-testid={`biotech-contact-${r.response_id}`}>
                            {r.biotech_primary_contact?.name && (
                              <span className="font-medium text-slate-600" title={r.biotech_primary_contact.designation || r.biotech_primary_contact.name}>
                                {r.biotech_primary_contact.name}
                              </span>
                            )}
                            {r.biotech_primary_contact?.email && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const em = r.biotech_primary_contact.email;
                                  navigator.clipboard.writeText(em)
                                    .then(() => toast.success(`Copied ${em}`))
                                    .catch(() => toast.error('Copy failed'));
                                }}
                                className="text-[#00709B] hover:underline cursor-pointer inline-flex items-center gap-0.5"
                                title="Click to copy email"
                                data-testid={`biotech-contact-email-${r.response_id}`}
                              >
                                ‹{r.biotech_primary_contact.email}›
                                <ClipboardCopy className="w-2.5 h-2.5 opacity-60" />
                              </button>
                            )}
                          </span>
                        )}
                        {isIntroduced && !isLost && (
                          <Badge
                            className="text-[9px] px-1.5 py-0 bg-emerald-100 text-emerald-700 border-emerald-200"
                            variant="outline"
                            title={
                              (r.introduced_asset_names || []).length
                                ? `Introduced via: ${(r.introduced_asset_names || []).join(', ')}`
                                : (r.introduced_source === 'derived_from_outreach'
                                    ? 'Auto-detected from sent emails'
                                    : 'Introduction made')
                            }
                            data-testid={`introduced-badge-${r.response_id}`}
                          >
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Introduced
                            {(r.introduced_asset_names || []).length > 0 && (
                              <span className="ml-1 text-[8px] font-normal text-emerald-800/80">
                                · {(r.introduced_asset_names || []).slice(0, 2).join(', ')}
                                {(r.introduced_asset_names || []).length > 2 && ` +${(r.introduced_asset_names || []).length - 2}`}
                              </span>
                            )}
                          </Badge>
                        )}
                        {isLost && (
                          <Badge className="text-[9px] px-1.5 py-0 bg-red-100 text-red-700 border-red-300 font-semibold uppercase tracking-wide" variant="outline" data-testid={`declined-badge-${r.response_id}`}>
                            Declined
                          </Badge>
                        )}
                        {r.biotech_company_id && (
                          <a
                            href={`/biotech/${r.biotech_company_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] text-[#00709B] hover:underline flex items-center gap-0.5 shrink-0"
                            data-testid={`view-biotech-${r.response_id}`}
                          >
                            <ExternalLink className="w-3 h-3" /> View
                          </a>
                        )}
                      </div>
                      {r.notes && (
                        isLost ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300 transition shrink-0"
                                title="Click to see why this biotech declined"
                                data-testid={`response-notes-${r.response_id}`}
                              >
                                <span className="text-red-500 leading-none">✕</span>
                                Reason
                                <ChevronDown className="w-3 h-3 opacity-60" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-96 p-0"
                              data-testid={`response-notes-popover-${r.response_id}`}
                            >
                              <div className="px-3 py-2 border-b border-red-100 bg-red-50/60">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700">
                                  Why {r.biotech_company_name || 'this biotech'} declined
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  Distilled from the pharma reply · {project?.client_company || 'pharma'}'s words rewritten in third-person
                                </p>
                              </div>
                              <div className="px-3 py-3 text-xs text-slate-800 leading-snug whitespace-pre-wrap">
                                {expandedReasonIds.has(r.response_id)
                                  ? r.notes
                                  : condenseDeclineReason(r.notes, project?.client_company)}
                              </div>
                              {r.notes.length > 200 && (
                                <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleReasonExpanded(r.response_id); }}
                                    className="text-[11px] text-sky-700 hover:text-sky-900 hover:underline font-medium"
                                    data-testid={`response-notes-toggle-${r.response_id}`}
                                  >
                                    {expandedReasonIds.has(r.response_id) ? '↑ Show distilled reason' : '↓ Show full reply (verbatim)'}
                                  </button>
                                </div>
                              )}
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <p className="text-xs mt-0.5 truncate text-slate-500"
                            title={r.notes}
                            data-testid={`response-notes-${r.response_id}`}>
                            {r.notes}
                          </p>
                        )
                      )}
                      {Array.isArray(r.interest_assets) && r.interest_assets.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`interest-assets-${r.response_id}`}>
                          <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-600">Discussion asset</span>
                          {r.interest_assets.map((a, idx) => (
                            <span
                              key={a.id || idx}
                              title={[
                                a.stage && `Stage: ${a.stage}`,
                                a.modality && `Modality: ${a.modality}`,
                                a.indication && `Indication: ${a.indication}`,
                                'Finalized as the asset under discussion with this account',
                              ].filter(Boolean).join(' · ')}
                              className="inline-flex items-center gap-1 px-1.5 py-0 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"
                            >
                              <FlaskConical className="w-2.5 h-2.5" />
                              {a.asset_name || '—'}{a.stage ? ` · ${a.stage}` : ''}
                            </span>
                          ))}
                          {r.biotech_company_id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openAssetEditor(r); }}
                              className="inline-flex items-center justify-center w-4 h-4 rounded text-slate-400 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Edit the finalized discussion asset(s)"
                              data-testid={`edit-assets-${r.response_id}`}
                            >
                              <Pencil className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {Array.isArray(r.suggested_assets) && r.suggested_assets.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`suggested-assets-${r.response_id}`}>
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">Likely fit</span>
                          {r.suggested_assets.map((a, idx) => (
                            <span
                              key={a.id || idx}
                              title={[
                                a.stage && `Stage: ${a.stage}`,
                                a.modality && `Modality: ${a.modality}`,
                                a.therapeutic_area && `TA: ${a.therapeutic_area}`,
                                a.indication && `Indication: ${a.indication}`,
                                a.fit_reasons?.length && `Matched on: ${a.fit_reasons.join(', ')}`,
                                'AI-suggested — click the pencil to finalize the actual discussion asset(s)',
                              ].filter(Boolean).join(' · ')}
                              className="inline-flex items-center gap-1 px-1.5 py-0 rounded bg-lime-50 text-lime-700 border border-lime-200 text-[10px]"
                            >
                              <FlaskConical className="w-2.5 h-2.5" />
                              {a.asset_name || '—'}
                            </span>
                          ))}
                          {r.biotech_company_id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openAssetEditor(r); }}
                              className="inline-flex items-center justify-center w-4 h-4 rounded text-slate-400 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Finalize the actual asset(s) being discussed — AI 'Likely fit' is only a starting point"
                              data-testid={`edit-assets-${r.response_id}`}
                            >
                              <Pencil className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {r.biotech_company_id
                        && !(Array.isArray(r.interest_assets) && r.interest_assets.length > 0)
                        && !(Array.isArray(r.suggested_assets) && r.suggested_assets.length > 0) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openAssetEditor(r); }}
                          className="inline-flex items-center gap-1 mt-1 text-[10px] text-slate-400 hover:text-emerald-700"
                          title="Select the asset(s) being discussed with this company"
                          data-testid={`tag-assets-${r.response_id}`}
                        >
                          <Pencil className="w-2.5 h-2.5" /> Tag discussion asset
                        </button>
                      )}
                      <p className="text-[10px] text-slate-400 mt-0.5">Added {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    {/* Divider */}
                    <div className="w-px self-stretch bg-slate-200" />
                    <Badge variant="outline"
                      className={`text-[10px] px-2 py-0.5 cursor-pointer hover:opacity-80 ${RESPONSE_COLORS[r.status] || ''}`}
                      onClick={() => {
                        setEditingResponseId(r.response_id);
                        setEditResponseStatus(r.status);
                        setEditResponseNotes(r.notes || '');
                      }}
                      data-testid={`response-status-${r.response_id}`}>
                      {r.status}
                    </Badge>
                    {/* Divider */}
                    <div className="w-px self-stretch bg-slate-200" />
                    {/* Status-aware primary action button:
                          • Prospect Added  → "Introduce Anonymously" (sounding-only, biotech identity withheld)
                          • Interest Shown  → "Connect Parties" (named tripartite intro, biotech in CC)
                          • Otherwise (Introduction Made / waiting / mid-funnel / closed) → no primary button.
                        Edge case: row was introduced anonymously and pharma never responded;
                        user can still re-fire by manually moving status back to "Prospect Added".
                    */}
                    {!isLost && !isDNC && r.status === 'Prospect Added' && (
                      <Button variant="outline" size="sm" className="h-7 px-2 text-[10px] text-[#00709B] border-[#00709B]/30 hover:bg-[#00709B]/5"
                        onClick={() => openIntroduce(r, 'anonymous')}
                        title="Send an anonymous asset sounding to the pharma — biotech identity withheld"
                        data-testid={`introduce-anon-response-${r.response_id}`}>
                        <Send className="w-3 h-3 mr-1" /> Introduce <span className="hidden sm:inline ml-0.5">Anonymously</span>
                      </Button>
                    )}
                    {!isLost && !isDNC && r.status === 'Interest Shown' && (
                      <Button variant="outline" size="sm" className="h-7 px-2 text-[10px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={() => openIntroduce(r, 'named')}
                        title="Pharma signalled interest — wire the biotech and pharma directly"
                        data-testid={`connect-parties-response-${r.response_id}`}>
                        <Send className="w-3 h-3 mr-1" /> Connect <span className="hidden sm:inline ml-0.5">Parties</span>
                      </Button>
                    )}
                    {/* Aug 2026 · DNC replaces the primary action with a
                        red disabled placeholder that links to the biotech
                        page so the user can review/remove the flag if
                        it's a mistake. Zero risk of accidental outreach. */}
                    {isDNC && (r.status === 'Prospect Added' || r.status === 'Interest Shown') && (
                      <a
                        href={r.biotech_company_id ? `/biotech/${r.biotech_company_id}` : '#'}
                        onClick={(e) => { if (!r.biotech_company_id) e.preventDefault(); }}
                        className="inline-flex items-center h-7 px-2 rounded border border-red-300 bg-red-50 text-[10px] text-red-700 hover:bg-red-100"
                        title={dncReason ? `Opted out: ${dncReason}` : 'Biotech opted out — outreach disabled'}
                        data-testid={`dnc-action-${r.response_id}`}
                      >
                        <ShieldAlert className="w-3 h-3 mr-1" />
                        Contact Blocked
                      </a>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                      onClick={() => setDeleteResponseId(r.response_id)} data-testid={`delete-response-${r.response_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </CardContent>
                </Card>
                {/* Per-asset sub-rows — render only when the user expanded this
                    biotech and the response has per_asset_statuses stamped.
                    Each entry is independently status-updatable; older
                    introductions (pre-asset-level stamping) just don't render
                    here, so the parent row's single status remains the source
                    of truth for those. */}
                {Array.isArray(r.per_asset_statuses) && r.per_asset_statuses.length > 0 && expandedResponseIds.has(r.response_id) && (
                  <div className="ml-12 mr-2 -mt-1 mb-1 border-l-2 border-slate-200 pl-3 space-y-1.5" data-testid={`per-asset-rows-${r.response_id}`}>
                    {r.per_asset_statuses.map(pa => (
                      <div key={pa.entry_id || pa.asset_id}
                        className="flex items-center gap-2 px-2.5 py-1.5 bg-white rounded-md border border-slate-100 hover:border-slate-200 transition-colors"
                        data-testid={`per-asset-row-${pa.entry_id || pa.asset_id}`}>
                        <FlaskConical className="w-3 h-3 text-violet-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <a
                              href={pa.asset_id ? `/biotech/${r.biotech_company_id}#asset-${pa.asset_id}` : '#'}
                              onClick={(e) => { if (!pa.asset_id) e.preventDefault(); }}
                              className="text-xs font-medium text-slate-800 hover:text-[#00709B] truncate"
                              title={pa.asset_name}
                            >
                              {pa.asset_name || '—'}
                            </a>
                            {pa.stage && <Badge variant="outline" className="text-[9px] px-1 py-0 text-slate-500 border-slate-200">{pa.stage}</Badge>}
                            {pa.modality && <span className="text-[9px] text-slate-400 truncate">· {pa.modality}</span>}
                          </div>
                          {pa.indication && (
                            <p className="text-[10px] text-slate-500 truncate" title={pa.indication}>{pa.indication}</p>
                          )}
                          {pa.status_notes && (
                            <p className="text-[10px] italic text-slate-400 mt-0.5 truncate" title={pa.status_notes}>{pa.status_notes}</p>
                          )}
                        </div>
                        <Badge variant="outline"
                          className={`text-[10px] px-1.5 py-0.5 cursor-pointer hover:opacity-80 ${RESPONSE_COLORS[pa.status] || ''}`}
                          onClick={() => {
                            setEditingResponseId(r.response_id);
                            setEditingEntryId(pa.entry_id);
                            setEditingEntryLabel(pa.asset_name || '');
                            setEditResponseStatus(pa.status || 'Introduction Made');
                            setEditResponseNotes(pa.status_notes || '');
                          }}
                          data-testid={`per-asset-status-${pa.entry_id || pa.asset_id}`}>
                          {pa.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== TAB: Outreach History ===== */}
      {activeTab === 'outreach-history' && (
        <Card className="border-slate-100" data-testid="outreach-history-tab">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Send className="w-4 h-4 text-sky-500" /> Outreach &amp; Deal Involvement
                {historyLoaded && <Badge variant="secondary" className="text-[10px] ml-1" data-testid="history-total-badge">{historyRecords.length}</Badge>}
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={historySearch} onChange={e => setHistorySearch(e.target.value)} placeholder="Search company or asset..." className="pl-8 h-8 text-xs w-[220px]" data-testid="history-search-input" />
                </div>
                <Select value={historyStatusFilter} onValueChange={setHistoryStatusFilter}>
                  <SelectTrigger className="h-8 text-xs w-[190px]" data-testid="history-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.keys(historyCounts).sort().map(s => (
                      <SelectItem key={s} value={s}>{s} ({historyCounts[s]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Every outreach touchpoint linked to {project.client_company || 'this account'} — intro emails, bulk sends and evaluations across all contacts at the company.
            </p>
          </CardHeader>
          <CardContent className="pt-1">
            {historyLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : historyRecords.length === 0 ? (
              <p className="text-xs text-slate-400 py-8 text-center" data-testid="history-empty-state">No outreach recorded for this account yet.</p>
            ) : (() => {
              const q = historySearch.trim().toLowerCase();
              const filtered = historyRecords.filter(r =>
                (historyStatusFilter === 'all' || r.status === historyStatusFilter) &&
                (!q || (r.company_name || '').toLowerCase().includes(q) || (r.asset_name || '').toLowerCase().includes(q))
              );
              return (
                <div className="border border-slate-100 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[1.6fr_1.3fr_1fr_1fr_0.7fr_70px] px-4 py-2 bg-slate-50 border-b border-slate-100 gap-2">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Biotech Company</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Type</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">On Behalf Of</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Date</span>
                    <span></span>
                  </div>
                  <div className="divide-y divide-slate-50 max-h-[560px] overflow-y-auto" data-testid="history-records-list">
                    {filtered.map((r, i) => (
                      <div key={r.id || i} className="grid grid-cols-[1.6fr_1.3fr_1fr_1fr_0.7fr_70px] px-4 py-2.5 items-center gap-2 hover:bg-slate-50/50" data-testid={`history-row-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{r.company_name || '—'}</p>
                          {r.asset_name && <p className="text-[10px] text-slate-400 truncate italic">· {r.asset_name}</p>}
                        </div>
                        <div className="min-w-0">
                          <Badge className={`text-[10px] w-fit ${RESPONSE_COLORS[r.status] || 'bg-slate-100 text-slate-600'}`}>{r.status || '—'}</Badge>
                          {r.status_notes && <p className="text-[10px] text-slate-400 italic mt-0.5 truncate">{r.status_notes}</p>}
                        </div>
                        <Badge variant="outline" className="text-[10px] w-fit">{r.source === 'bulk_outreach' ? 'Bulk Send' : r.source === 'il_outreach' ? 'Project Email' : 'Intro / CRM'}</Badge>
                        <p className="text-[11px] text-slate-500 truncate">{r.on_behalf_of || '—'}</p>
                        <p className="text-[11px] text-slate-500 whitespace-nowrap">{r.date ? new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</p>
                        <span className="flex items-center gap-1.5 justify-end">
                          {(r.email_body || r.email_subject) && (
                            <button onClick={() => setViewEmailRecord(r)} title="View sent email copy" className="text-slate-400 hover:text-[#00709B]" data-testid={`history-view-email-${i}`}>
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {r.biotech_company_id && (
                            <a href={`/biotech/${r.biotech_company_id}`} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 text-xs hover:underline">View</a>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ===== TAB: Company Overview ===== */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {companyProfile ? (
            <>
              {/* Company Brief */}
              {companyProfile.company_brief && (
                <Card className="border-slate-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" /> Company Brief
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-slate-600 leading-relaxed">{companyProfile.company_brief}</p>
                  </CardContent>
                </Card>
              )}

              {/* Therapeutic Interests */}
              {companyProfile.therapeutic_interests?.length > 0 && (
                <Card className="border-slate-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <FlaskConical className="w-4 h-4 text-violet-500" /> Therapeutic Interests
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 flex flex-wrap gap-1.5">
                    {companyProfile.therapeutic_interests.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] px-2 py-0.5">{t}</Badge>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Corporate Decks */}
              {companyProfile.corporate_decks?.length > 0 && (
                <Card className="border-slate-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-500" /> Corporate Presentations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    {companyProfile.corporate_decks.map((d, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded border border-slate-100">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-sm text-slate-700 truncate flex-1">{d.filename}</span>
                        <button onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/company-profiles/${companyProfile.id}/download-deck/${encodeURIComponent(d.filename)}`, { responseType: 'blob', withCredentials: true });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = d.filename;
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error('Failed to download file'); }
                        }} className="text-xs text-[#00709B] hover:underline flex items-center gap-1 cursor-pointer">
                          <ExternalLink className="w-3 h-3" /> Download
                        </button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Pharma Contacts at this company */}
              {pharmaContacts.length > 0 && (
                <Card className="border-slate-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-500" /> Contacts at {project.client_company}
                      <Badge variant="secondary" className="text-[10px] ml-1">{pharmaContacts.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-1.5">
                    {pharmaContacts.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded border border-slate-50 hover:border-slate-200 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                          <Users className="w-3.5 h-3.5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                          <p className="text-[10px] text-slate-500 truncate">
                            {c.email}{c.designation ? ` · ${c.designation}` : ''}
                          </p>
                        </div>
                        {c.id && (
                          <button onClick={() => navigate(`/pharma/${c.id}`)} className="text-[10px] text-[#00709B] hover:underline flex items-center gap-0.5">
                            <ExternalLink className="w-3 h-3" /> Detail
                          </button>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-dashed border-slate-200">
              <CardContent className="py-10 text-center">
                <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No company profile found for "{project.client_company}"</p>
                <p className="text-xs text-slate-400 mt-1">Create a company profile in Pharma Companies to see details here</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== DIALOGS ===== */}

      {/* Add Prospect Dialog — step 1: search biotech · step 2: tag interest asset(s) */}
      <Dialog open={showAddResponse} onOpenChange={(open) => { if (!open) resetProspectDialog(); else setShowAddResponse(true); }}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[88vh] overflow-x-hidden overflow-y-auto" data-testid="add-response-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Prospect</DialogTitle>
            <p className="text-xs text-slate-500">
              {prospectStep === 1
                ? 'Search for a biotech company to add as an active prospect'
                : <>Tag the asset(s) of interest at <span className="font-medium text-slate-700">{prospectBiotech?.company_name}</span></>}
            </p>
          </DialogHeader>
          {prospectStep === 1 ? (
          <div className="space-y-3 py-2 min-w-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={responseSearch} onChange={e => handleResponseSearch(e.target.value)}
                placeholder="Search by name or email (e.g. @lnlbio.com)..." className="pl-8 h-9 text-sm" autoFocus data-testid="response-search-input" />
              {searchingResponse && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />}
            </div>
            {responseSearchResults.length > 0 && (
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto overflow-x-hidden divide-y divide-slate-50">
                {responseSearchResults.map((r, i) => (
                  <button key={i} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2.5"
                    onClick={() => selectProspectBiotech(r)} data-testid={`response-result-${i}`}>
                    <div className="w-8 h-8 rounded bg-cyan-50 flex items-center justify-center shrink-0">
                      <Building2 className="w-3.5 h-3.5 text-cyan-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.company_name}</p>
                      {r.matched_email && (
                        <p className="text-[10px] text-cyan-600 truncate">
                          {r.matched_contact ? `${r.matched_contact} · ` : ''}{r.matched_email}
                        </p>
                      )}
                      {r.location && <p className="text-[10px] text-slate-400">{r.location}</p>}
                    </div>
                    {r.is_my_company && (
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          ) : (
          <div className="space-y-3 py-2 min-w-0" data-testid="prospect-asset-step">
            <p className="text-[11px] text-slate-500 leading-snug">
              Which asset(s) form the basis of this introduction? They&apos;ll be tagged on the prospect and pre-selected when you draft the intro email to {project?.client_company || 'the pharma'}. Optional — leave unticked if not asset-specific.
            </p>
            {prospectAssetsLoading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : prospectAssets.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No assets on file for this biotech — you can still add the prospect without tagging.</p>
            ) : (
              <>
              {prospectSiblingCount > 1 && (
                <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded px-2 py-1" data-testid="prospect-sibling-hint">
                  Assets merged from {prospectSiblingCount} matching company records with this name.
                </p>
              )}
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto overflow-x-hidden divide-y divide-slate-50">
                {prospectAssets.map((a) => (
                  <label key={a.id} className="flex items-start gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer min-w-0" data-testid={`prospect-asset-${a.id}`}>
                    <input type="checkbox" checked={!!prospectSelectedAssets[a.id]}
                      onChange={e => setProspectSelectedAssets(prev => ({ ...prev, [a.id]: e.target.checked }))}
                      className="mt-1 h-3.5 w-3.5 rounded border-slate-300 text-[#00709B] focus:ring-[#00709B]" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-slate-800 truncate">{a.asset_name || 'Unnamed asset'}</span>
                      <span className="block text-[10px] text-slate-500 truncate">
                        {[a.stage, a.indication || a.therapeutic_area, a.modality].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              </>
            )}
            <div className="flex items-center justify-between gap-2 pt-1 min-w-0">
              <Button variant="ghost" size="sm" className="text-xs text-slate-500 shrink-0"
                onClick={() => { setProspectStep(1); setProspectBiotech(null); }} data-testid="prospect-back-btn">
                ← Back to search
              </Button>
              <Button size="sm" className="text-xs bg-[#0A1128] hover:bg-slate-800 text-white shrink-0"
                onClick={addBiotechResponse} disabled={addingProspect} data-testid="prospect-add-btn">
                {addingProspect ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                Add Prospect{Object.values(prospectSelectedAssets).filter(Boolean).length > 0 ? ` (${Object.values(prospectSelectedAssets).filter(Boolean).length} tagged)` : ''}
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Response Status */}
      <Dialog open={!!editingResponseId} onOpenChange={(open) => { if (!open) { setEditingResponseId(null); setEditingEntryId(null); setEditingEntryLabel(''); } }}>
        <DialogContent className={`${rejectionStep === 2 ? 'max-w-3xl' : 'max-w-sm'} max-h-[92vh] overflow-y-auto`} data-testid="edit-response-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {rejectionStep === 2
                ? `Send update to ${rejectionDraft?.biotech_company_name || 'biotech'}`
                : editingEntryId ? `Update Status — ${editingEntryLabel || 'Asset'}` : 'Update Response Status'}
            </DialogTitle>
            {editingEntryId && rejectionStep === 1 && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                This update applies only to <span className="font-medium text-slate-700">{editingEntryLabel}</span>. Other assets for this biotech keep their current status.
              </p>
            )}
            {rejectionStep === 2 && rejectionDraft && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                {revealPharmaIdentity
                  ? <>The pharma <span className="font-medium text-slate-700">{project?.client_company || ''}</span> is named in this draft (biotech already knows).</>
                  : <>The pharma's identity stays confidential — they're referred to as "the prospective partner".</>
                }{' '}
                Will send to <span className="font-medium text-slate-700">{rejectionDraft.to_name || rejectionDraft.to_email}</span>.
              </p>
            )}
          </DialogHeader>
          {rejectionStep === 1 ? (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-medium text-slate-600">Status</Label>
                <Select value={editResponseStatus} onValueChange={setEditResponseStatus}>
                  <SelectTrigger className="mt-1 h-9 text-sm" data-testid="select-response-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{RESPONSE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editResponseStatus === 'Not Interested' ? (
                <div>
                  <Label className="text-xs font-medium text-slate-600">
                    Pharma's rejection email <span className="font-normal text-slate-400">(paste here — AI will draft a warm update for the biotech)</span>
                  </Label>
                  <Textarea
                    value={rejectionEmailText}
                    onChange={e => setRejectionEmailText(e.target.value)}
                    placeholder="Paste the full rejection email from the pharma BD lead so the AI can extract the reason and tell the biotech why this conversation didn't progress. The pharma's identity is NEVER revealed to the biotech."
                    className="mt-1 text-sm min-h-[160px] font-mono"
                    data-testid="rejection-email-input"
                  />
                  <label className="mt-2 flex items-start gap-2 cursor-pointer select-none" data-testid="reveal-pharma-toggle-row">
                    <input
                      type="checkbox"
                      checked={revealPharmaIdentity}
                      onChange={e => setRevealPharmaIdentity(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      data-testid="reveal-pharma-checkbox"
                    />
                    <span className="text-[11px] text-slate-600 leading-snug">
                      <span className="font-medium text-slate-700">Name the pharma ({project?.client_company || 'this account'}) in the email.</span>
                      <span className="block text-slate-400">
                        Tick this only if the biotech already knows who we pitched to (e.g. you mentioned the pharma in the intro thread or via the shared AI mapping link). Off by default for confidentiality.
                      </span>
                    </span>
                  </label>
                  <p className="text-[10px] text-slate-400 mt-1">
                    On Save: status is updated, then Claude paraphrases the reason and drafts a polite follow-up to the biotech contact. You review &amp; send in the next step. Leave blank to just save the status without sending anything.
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-medium text-slate-600">Notes</Label>
                  <Textarea value={editResponseNotes} onChange={e => setEditResponseNotes(e.target.value)}
                    placeholder="Optional notes..." className="mt-1 text-sm min-h-[60px]" data-testid="response-notes" />
                </div>
              )}
            </div>
          ) : (
            // Step 2 — AI-drafted close-the-loop preview
            <div className="space-y-4 py-2" data-testid="rejection-draft-review">
              <div className="grid grid-cols-[80px_1fr] items-center gap-3">
                <Label className="text-xs font-medium text-slate-500 text-right">To</Label>
                <Input
                  value={rejectionDraft?.to_email || ''}
                  onChange={e => setRejectionDraft(prev => ({ ...prev, to_email: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="rejection-to-email"
                />
              </div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-3">
                <Label className="text-xs font-medium text-slate-500 text-right">Subject</Label>
                <Input
                  value={rejectionDraft?.subject || ''}
                  onChange={e => setRejectionDraft(prev => ({ ...prev, subject: e.target.value }))}
                  className="h-9 text-sm font-medium"
                  data-testid="rejection-subject"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Body</Label>
                <Textarea
                  value={rejectionDraft?.body || ''}
                  onChange={e => setRejectionDraft(prev => ({ ...prev, body: e.target.value }))}
                  className="text-[15px] min-h-[340px] leading-relaxed p-4 font-serif"
                  style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif" }}
                  data-testid="rejection-body"
                />
                <p className="text-[10px] text-slate-400 mt-1.5">Edit freely. The system appends Dr. Amit Dang's signature automatically.</p>
              </div>
            </div>
          )}
          <DialogFooter className={rejectionStep === 1 ? '' : 'sm:justify-between gap-2'}>
            {rejectionStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => {
                  setEditingResponseId(null);
                  setEditingEntryId(null);
                  setEditingEntryLabel('');
                  setRejectionStep(1);
                  setRejectionEmailText('');
                  setRejectionDraft(null);
                  setRevealPharmaIdentity(false);
                }} className="h-9 text-sm">Cancel</Button>
                {/* "Save status only" — skip the AI biotech-followup draft
                    even when a rejection email is pasted. Useful when the
                    user wants to record the outcome internally without
                    notifying the biotech (e.g., the biotech already knows,
                    or they're tracking this silently). User request 2026-02:
                    "i should be able to simply change the status without
                    sending email to biotech." */}
                <Button
                  variant="outline"
                  onClick={() => updateResponseStatus({ skipBiotechEmail: true })}
                  disabled={saving || rejectionDraftLoading}
                  className="h-9 text-sm border-slate-300"
                  data-testid="save-status-only"
                  title="Update status without drafting any email to the biotech"
                >
                  {(saving || rejectionDraftLoading) && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                  Save status only
                </Button>
                <Button onClick={() => updateResponseStatus()} disabled={saving || rejectionDraftLoading} className="bg-[#0A1128] text-white hover:bg-slate-800 h-9 text-sm" data-testid="save-response-status">
                  {(saving || rejectionDraftLoading) && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                  {editResponseStatus === 'Not Interested' && rejectionEmailText.trim().length >= 20 ? 'Save & Draft Follow-up' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                {/* Left group: navigation back. Esc / X close the dialog
                    entirely so we don't need a separate Cancel here. */}
                <Button
                  variant="ghost"
                  onClick={() => setRejectionStep(1)}
                  className="h-9 text-sm text-slate-500 hover:text-slate-900"
                  data-testid="rejection-back-btn"
                >
                  ← Back
                </Button>
                {/* Right group: send actions, visually separated from Back */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={sendTestRejectionFollowup}
                    disabled={rejectionTestSending || rejectionSending}
                    className="h-9 text-sm border-sky-300 text-sky-700 hover:bg-sky-50"
                    data-testid="rejection-test-send-btn"
                    title="Send a test copy to your own inbox so you can preview it in Gmail before firing at the biotech"
                  >
                    {rejectionTestSending && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                    Send test to me
                  </Button>
                  <Button
                    onClick={sendRejectionFollowup}
                    disabled={rejectionSending || rejectionTestSending}
                    className="bg-emerald-600 text-white hover:bg-emerald-700 h-9 text-sm"
                    data-testid="rejection-send-btn"
                  >
                    {rejectionSending && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                    Send to biotech
                  </Button>
                </div>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Response */}
      <AlertDialog open={!!deleteResponseId} onOpenChange={() => setDeleteResponseId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Response?</AlertDialogTitle>
            <AlertDialogDescription>This biotech will be untagged from this account's response list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteResponse} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-response">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Finalize discussion assets (Jul 2026) */}
      <Dialog open={!!assetEditDialog} onOpenChange={(v) => { if (!v) setAssetEditDialog(null); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="finalize-assets-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Finalize discussion asset(s)
            </DialogTitle>
            <p className="text-xs text-slate-500">
              {assetEditDialog?.response?.biotech_company_name} — select the asset(s) actually being
              discussed with {project?.client_company || 'this account'}. AI "Likely fit" tags are only a
              starting point; your selection here becomes the source of truth across Accounts, Outreach
              and introduction emails.
            </p>
          </DialogHeader>
          {assetEditDialog?.loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading assets…
            </div>
          ) : (
            <div className="space-y-3 py-1">
              {(assetEditDialog?.assets || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic py-2">
                  No assets on file for this company yet — add one below.
                </p>
              ) : (
                <div className="space-y-1 max-h-[320px] overflow-y-auto pr-1">
                  {(assetEditDialog?.assets || []).map((a) => (
                    <label
                      key={a.id}
                      className={`flex items-start gap-2 px-2.5 py-2 rounded-md border cursor-pointer transition-colors ${
                        assetEditDialog.selected.has(a.id)
                          ? 'border-emerald-300 bg-emerald-50/60'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`finalize-asset-option-${a.id}`}
                    >
                      <Checkbox
                        checked={assetEditDialog.selected.has(a.id)}
                        onCheckedChange={() => toggleAssetSelection(a.id)}
                        className="mt-0.5"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                          <FlaskConical className="w-3 h-3 text-slate-400 shrink-0" />
                          {a.asset_name || 'Unnamed asset'}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {[a.stage, a.modality, a.indication || a.therapeutic_area].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Manual add — asset not linked to the company yet */}
              <div className="border-t border-slate-100 pt-3">
                <p className="text-[11px] font-semibold text-slate-600 mb-1.5">
                  Asset not listed? Add it to {assetEditDialog?.response?.biotech_company_name}:
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Asset name (e.g. BB-102)"
                    value={assetEditDialog?.newName || ''}
                    onChange={(e) => setAssetEditDialog(prev => ({ ...prev, newName: e.target.value }))}
                    className="h-8 text-xs flex-1"
                    data-testid="finalize-new-asset-name"
                  />
                  <Input
                    placeholder="Stage (optional)"
                    value={assetEditDialog?.newStage || ''}
                    onChange={(e) => setAssetEditDialog(prev => ({ ...prev, newStage: e.target.value }))}
                    className="h-8 text-xs w-32"
                    data-testid="finalize-new-asset-stage"
                  />
                  <Button
                    size="sm" variant="outline" className="h-8 text-xs"
                    onClick={createManualAsset}
                    disabled={assetEditDialog?.creating || !(assetEditDialog?.newName || '').trim()}
                    data-testid="finalize-add-asset-btn"
                  >
                    {assetEditDialog?.creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" /> Add</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
            <span className="text-[11px] text-slate-500">
              {assetEditDialog?.selected?.size || 0} selected
              {assetEditDialog?.selected?.size === 0 && ' — saving with none reverts to AI suggestions'}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setAssetEditDialog(null)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={saveFinalAssets}
                disabled={assetEditDialog?.saving || assetEditDialog?.loading}
                data-testid="finalize-assets-save-btn"
              >
                {assetEditDialog?.saving
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
                  : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Save discussion asset(s)</>}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg" data-testid="edit-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-600">Account Name</Label>
              <Input value={editForm.project_name || ''} onChange={e => setEditForm(f => ({ ...f, project_name: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="edit-account-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Client Company</Label>
                <Input value={editForm.client_company || ''} onChange={e => setEditForm(f => ({ ...f, client_company: e.target.value }))} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Status</Label>
                <Select value={editForm.status || 'Active'} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Contact Name</Label>
                <Input value={editForm.client_name || ''} onChange={e => setEditForm(f => ({ ...f, client_name: e.target.value }))} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Contact Email</Label>
                <Input value={editForm.client_email || ''} onChange={e => setEditForm(f => ({ ...f, client_email: e.target.value }))} className="mt-1 h-9 text-sm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)} className="h-9 text-sm">Cancel</Button>
            <Button onClick={handleEditProject} disabled={saving} className="bg-[#0A1128] text-white hover:bg-slate-800 h-9 text-sm" data-testid="save-edit-account">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Account Introduce Dialog ===== */}
      <Dialog open={!!introduceResponse} onOpenChange={() => setIntroduceResponse(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="introduce-dialog">
          {/* Header banner — clearer hierarchy + step indicator */}
          <div className={`px-6 py-4 border-b ${introduceMode === 'anonymous' ? 'bg-gradient-to-r from-amber-50 to-amber-50/30 border-amber-200' : 'bg-gradient-to-r from-sky-50 to-sky-50/30 border-sky-200'}`}>
            <DialogHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {introduceMode === 'anonymous'
                    ? <Send className="w-4 h-4 text-amber-700" />
                    : <Users className="w-4 h-4 text-sky-700" />}
                  {introduceMode === 'anonymous' ? 'Anonymous Asset Sounding' : 'Connect Pharma & Biotech'}
                  {introduceMode === 'anonymous' && (
                    <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200" variant="outline">Identity withheld</Badge>
                  )}
                </DialogTitle>
                <Badge variant="outline" className="text-[10px] font-medium bg-white border-slate-300 text-slate-600">
                  Step {introduceStep} of 2 · {introduceStep === 1 ? 'Configure' : 'Preview & Send'}
                </Badge>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {introduceStep === 1
                  ? (introduceMode === 'anonymous'
                      ? <>Sound out <strong>{project?.client_name || project?.client_company}</strong> on an asset opportunity. <strong>{introduceBiotech?.company_name || introduceResponse?.biotech_company_name}</strong>'s identity stays anonymous until interest is confirmed.</>
                      : <>Introduce <strong>{introduceBiotech?.company_name || introduceResponse?.biotech_company_name}</strong> to <strong>{project?.client_name || project?.client_company}</strong></>)
                  : (introduceMode === 'anonymous'
                      ? <>Email goes only to <strong>{introduceCcName || introduceCcEmail}</strong> — biotech is not CC'd.</>
                      : <>Email to <strong>{introduceCcName || introduceCcEmail}</strong>, biotech contacts CC'd.</>)}
              </p>
            </DialogHeader>
          </div>

          {introduceLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : introduceStep === 1 ? (
            <div className="px-6 py-5 space-y-5">
              {/* Recipient card — prominent so user always knows who'll get
                  this. Was hidden behind misleading "CC Name / CC Email"
                  labels before. */}
              <section data-testid="introduce-recipient-section">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Send to (Pharma Contact)</Label>
                </div>
                <div className="grid grid-cols-[1fr_1.5fr] gap-3">
                  <Input value={introduceCcName} onChange={e => setIntroduceCcName(e.target.value)}
                    className="h-10 text-sm" placeholder="Name (e.g., Jesse Hwang)" data-testid="introduce-cc-name" />
                  <Input value={introduceCcEmail} onChange={e => setIntroduceCcEmail(e.target.value)}
                    className="h-10 text-sm font-mono" placeholder="Email (e.g., jhwang27@its.jnj.com)" data-testid="introduce-cc-email" />
                </div>
              </section>

              {/* Asset selection — card-style list with stage front-and-centre,
                  indications truncated with a +N expander so 14-indication
                  oncology assets don't blow up the modal height. */}
              <section data-testid="introduce-assets-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FlaskConical className="w-3.5 h-3.5 text-slate-400" />
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Assets to mention</Label>
                  </div>
                  <span className="text-[10px] text-slate-400">{Object.values(introduceSelectedAssets).filter(Boolean).length} of {introduceAssets.length} selected</span>
                </div>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
                  {introduceAssets.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">No assets found for this company</p>
                  ) : introduceAssets.map(a => {
                    const checked = !!introduceSelectedAssets[a.id];
                    const inds = (a.indication || '').split(',').map(x => x.trim()).filter(Boolean);
                    const showInds = inds.slice(0, 3);
                    const extra = inds.length - showInds.length;
                    return (
                      <label key={a.id}
                        className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-amber-50/40' : 'hover:bg-slate-50'}`}
                        data-testid={`asset-select-${a.id}`}>
                        <Checkbox checked={checked}
                          onCheckedChange={v => setIntroduceSelectedAssets(prev => ({ ...prev, [a.id]: v }))} className="shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">{a.asset_name}</span>
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0 bg-slate-50">{a.stage || 'Stage unknown'}</Badge>
                            {a.modality && <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0 bg-violet-50 text-violet-700 border-violet-200">{a.modality}</Badge>}
                          </div>
                          {inds.length > 0 && (
                            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                              {showInds.join(' · ')}
                              {extra > 0 && <span className="ml-1 text-[10px] text-slate-400" title={inds.join(', ')}>+{extra} more</span>}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>

              {/* Additional context — full width, taller, clearer placeholder */}
              <section data-testid="introduce-context-section">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-3.5 h-3.5 text-slate-400" />
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Additional context <span className="font-normal normal-case text-slate-400 tracking-normal">(optional — fed into the AI draft)</span>
                  </Label>
                </div>
                <textarea value={introduceContext} onChange={e => setIntroduceContext(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[80px] max-h-[160px] resize-y focus:outline-none focus:ring-1 focus:ring-amber-300 placeholder:text-slate-400 leading-relaxed"
                  placeholder="e.g., They showed strong interest in Phase 2 oncology at ChinaBio 2026. Or: They are available Tue/Wed for a deeper call."
                  data-testid="introduce-context" />
              </section>

              {/* Footer actions — full-width primary button so it's obvious */}
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => setIntroduceResponse(null)} className="h-9 text-xs" data-testid="introduce-cancel-btn">Cancel</Button>
                <Button onClick={handleIntroduceDraft}
                  disabled={introduceDrafting || Object.values(introduceSelectedAssets).filter(Boolean).length === 0 || !introduceCcEmail.trim()}
                  className={`h-9 px-5 text-sm font-medium text-white ${introduceMode === 'anonymous' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#00709B] hover:bg-[#005a7a]'}`}
                  data-testid="introduce-draft-btn">
                  {introduceDrafting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Drafting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Draft Email with AI</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="px-6 py-5 space-y-3">
              {/* TO: Pharma Contact */}
              {introduceCcEmail && (
                <div className="flex items-center gap-2 px-3 py-2 bg-sky-50 rounded-lg border border-sky-200">
                  <Mail className="w-3.5 h-3.5 text-sky-600" />
                  <span className="text-xs text-sky-700 font-medium">To: {introduceCcName} &lt;{introduceCcEmail}&gt;</span>
                </div>
              )}

              {/* CC: Biotech Contacts — hidden in anonymous mode (the whole
                  point of the sounding is that the biotech doesn't get
                  CC'd until the pharma signals interest). */}
              {introduceMode === 'named' ? (
                <div>
                  <Label className="text-xs font-medium text-slate-600">CC (Biotech Contacts)</Label>
                  <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {introduceContacts.filter(c => c.email).map((c, i) => (
                      <label key={i} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={!!introduceSelectedContacts[c.email]}
                          onCheckedChange={v => setIntroduceSelectedContacts(prev => ({ ...prev, [c.email]: v }))} />
                        <span className="text-sm text-slate-700">{c.name || c.email}</span>
                        <span className="text-[10px] text-slate-400 ml-auto">{c.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="anonymous-cc-notice">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800">
                    <strong>No CC.</strong> Biotech identity stays withheld in this email. After {project?.client_name || 'the pharma'} signals interest, change status to <em>Interest Shown</em> and use <em>Connect Parties</em> to make the named introduction.
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs font-medium text-slate-600">Subject</Label>
                  <Input value={introduceSubject} onChange={e => setIntroduceSubject(e.target.value)}
                    className="mt-1 h-9 text-sm" data-testid="introduce-subject" />
                </div>
                <div className="pt-6">
                  <SpamCheckChip
                    subject={introduceSubject}
                    bodyHtml={introduceBody}
                    onChange={(s, b) => { setIntroduceSubject(s); setIntroduceBody(b); }}
                    testId="introduce-spamcheck"
                  />
                </div>
              </div>

              {/* Body */}
              <div>
                <Label className="text-xs font-medium text-slate-600">Email Body</Label>
                <Textarea value={introduceBody} onChange={e => setIntroduceBody(e.target.value)}
                  className="mt-1 text-sm min-h-[180px] font-serif leading-relaxed" data-testid="introduce-body" />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setIntroduceStep(1)} className="h-8 text-xs">Back</Button>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={handleIntroduceTestSend} disabled={introduceTestSending}
                  className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="introduce-test-btn">
                  {introduceTestSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</> : <><Mail className="w-3.5 h-3.5 mr-1" /> Test to Me</>}
                </Button>
                <Button size="sm" onClick={handleIntroduceSend} disabled={introduceSending}
                  className="h-8 text-xs bg-[#00709B] text-white hover:bg-[#005a7a]" data-testid="introduce-send-btn">
                  {introduceSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</> : <><Send className="w-3.5 h-3.5 mr-1" /> Send via Gmail</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    {/* Status Update Email Dialog */}
    <Dialog open={showNextMatching} onOpenChange={(v) => { if (!v) setShowNextMatching(false); }}>
      <DialogContent className="max-w-md" data-testid="next-matching-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag className="w-4 h-4 fill-emerald-500 text-emerald-600" /> Run matching next</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Surfaces this account in the Accounts <span className="font-medium text-slate-700">"Lined up for matching"</span> strip and the Dashboard card. Clears automatically when you open Send Update for it.</p>
        <Textarea value={nextMatchingNote} onChange={e => setNextMatchingNote(e.target.value)} rows={3} placeholder="Optional note — e.g. wants oncology Ph2 shortlist by Friday" className="text-sm" data-testid="next-matching-note-input" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setShowNextMatching(false)}>Cancel</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveNextMatching} disabled={savingNextMatching} data-testid="next-matching-save-btn">
            {savingNextMatching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Flag className="w-3 h-3 mr-1" />} Line up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showStatusUpdate} onOpenChange={(v) => { if (!v) { setShowStatusUpdate(false); setStatusUpdateStep(1); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-violet-600" />
            Send Status Update to {project?.client_name || project?.client_company}
          </DialogTitle>
        </DialogHeader>

        {statusUpdateStep === 1 ? (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              This will generate an email summarizing all introductions and their current statuses for {project?.client_company}.
              Add any additional context or specific points you want included.
            </p>
            <div>
              <Label className="text-xs">Additional context (optional)</Label>
              <Textarea
                value={statusUpdatePrompt}
                onChange={e => setStatusUpdatePrompt(e.target.value)}
                placeholder="e.g., I want to highlight that we are still waiting on responses from 3 companies, and ask Hassan to prioritise which ones to follow up on..."
                rows={4}
                className="text-sm mt-1"
                data-testid="status-update-prompt"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowStatusUpdate(false)} className="text-xs">Cancel</Button>
              <Button size="sm" onClick={handleDraftStatusUpdate} disabled={statusUpdateDrafting}
                className="text-xs bg-violet-600 hover:bg-violet-700 text-white" data-testid="draft-status-update-btn">
                {statusUpdateDrafting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Drafting...</> : <><Sparkles className="w-3 h-3 mr-1" /> Draft Update</>}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">To</Label>
              <Input value={`${pharmaContacts[0]?.name || project?.client_name || ''} <${pharmaContacts[0]?.email || ''}>`} readOnly className="text-sm mt-1 bg-slate-50" />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={statusUpdateSubject} onChange={e => setStatusUpdateSubject(e.target.value)} className="text-sm mt-1" data-testid="status-update-subject" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Email Body</Label>
                <button
                  type="button"
                  onClick={() => setStatusUpdatePreviewMode(!statusUpdatePreviewMode)}
                  className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"
                  data-testid="toggle-preview-btn"
                >
                  {statusUpdatePreviewMode ? <><Pencil className="w-3 h-3" /> Edit</> : <><Eye className="w-3 h-3" /> Preview</>}
                </button>
              </div>
              {statusUpdatePreviewMode ? (
                <div
                  className="border rounded-md p-4 text-sm font-light bg-white min-h-[220px] max-h-[400px] overflow-y-auto"
                  style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '13px', lineHeight: '1.7', color: '#333' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((statusUpdateBody || '').replace(/\n/g, '<br>')) }}
                  data-testid="status-update-preview"
                />
              ) : (
                <Textarea
                  value={statusUpdateBody}
                  onChange={e => setStatusUpdateBody(e.target.value)}
                  rows={14}
                  className="text-sm mt-1 font-light"
                  data-testid="status-update-body"
                />
              )}
            </div>
            {/* Recipient pill — always visible so the user can confirm the
                canonical contact BEFORE clicking Send. Prevents the
                "Dear Zafeirios, sent to brett.roberts" mismatch bug. */}
            {(() => {
              const projectEmail = (project?.client_email || '').trim();
              const projectName = (project?.client_name || '').trim();
              const recipientEmail = projectEmail || pharmaContacts[0]?.email || '';
              const recipientName = projectEmail ? projectName : (pharmaContacts[0]?.name || projectName);
              const isFallback = !projectEmail && !!pharmaContacts[0]?.email;
              if (!recipientEmail) {
                return (
                  <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="status-update-no-recipient">
                    <strong>No recipient set.</strong> Edit this account to add a contact email before sending.
                  </div>
                );
              }
              return (
                <div className={`rounded-md border px-3 py-2 text-xs ${isFallback ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-violet-200 bg-violet-50 text-violet-800'}`} data-testid="status-update-recipient-pill">
                  <span className="font-medium">Will send to:</span>{' '}
                  <span className="font-semibold">{recipientName || recipientEmail}</span>
                  {recipientName && <span className="text-slate-500"> &lt;{recipientEmail}&gt;</span>}
                  {isFallback && (
                    <span className="block text-[10px] mt-0.5">⚠️ Account has no designated contact — falling back to first company contact. Set a canonical contact via Edit Account.</span>
                  )}
                </div>
              );
            })()}
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => { setStatusUpdateStep(1); setStatusUpdatePreviewMode(false); }} className="text-xs">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back
              </Button>
              <Button variant="outline" size="sm" onClick={async () => {
                try {
                  setStatusUpdateSending(true);
                  const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
                  const myEmail = me.data?.email;
                  if (!myEmail) { toast.error('Could not find your email'); return; }
                  await axios.post(`${API}/gmail/send`, {
                    to: myEmail,
                    subject: `[TEST] ${statusUpdateSubject}`,
                    body: statusUpdateBody,
                  }, { withCredentials: true });
                  toast.success(`Test email sent to ${myEmail}`);
                } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send test'); }
                finally { setStatusUpdateSending(false); }
              }} disabled={statusUpdateSending}
                className="text-xs text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="send-test-update-btn">
                {statusUpdateSending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</> : <><Mail className="w-3 h-3 mr-1" /> Send Test to Me</>}
              </Button>
              <Button size="sm" onClick={handleSendStatusUpdate} disabled={statusUpdateSending}
                className="text-xs bg-[#00709B] hover:bg-[#005A7A] text-white" data-testid="send-status-update-btn">
                {statusUpdateSending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</> : <><Send className="w-3 h-3 mr-1" /> Send Update</>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {viewEmailRecord && <SentEmailDialog record={viewEmailRecord} onClose={() => setViewEmailRecord(null)} />}

    </div>
  );
}
